import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { RichTextEditor } from "../components/RichTextEditor";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, ShieldAlert, Twitch, Gift, CalendarDays, CalendarClock, Pencil, Radio, Type, Image as ImageIcon, Users, UserCog, HandCoins, Webhook, Timer, Globe, Youtube, ShieldCheck, Megaphone, RotateCcw, Bold, Italic, Underline, Strikethrough, EyeOff, Link2, Heading1, Quote, List } from "lucide-react";

const TAB_GROUPS = [
  {
    group: "Contenuti", testid: "contenuti",
    items: [
      { id: "partner", label: "Streamer Partner", icon: Twitch },
      { id: "media", label: "Foto e Video", icon: ImageIcon },
      { id: "testi", label: "Testi Sito Web", icon: Type },
      { id: "youtube", label: "YouTube", icon: Youtube },
      { id: "sito", label: "Logo & Dominio", icon: Globe },
    ],
  },
  {
    group: "Community", testid: "community",
    items: [
      { id: "giveaway", label: "Giveaway", icon: Gift },
      { id: "eventi", label: "Eventi", icon: CalendarDays },
      { id: "eventi-gaming", label: "Eventi Gaming", icon: Radio },
      { id: "programmazione", label: "Programmazione Live", icon: CalendarClock },
      { id: "live-notify", label: "Notifica Live", icon: Megaphone },
      { id: "utenti", label: "Utenti", icon: UserCog },
      { id: "staff", label: "Staff", icon: Users },
    ],
  },
  {
    group: "Sicurezza", testid: "sicurezza",
    items: [
      { id: "accessi", label: "Accessi Utenti", icon: ShieldCheck },
    ],
  },
  {
    group: "Sistema", testid: "sistema",
    items: [
      { id: "paypal", label: "PayPal", icon: HandCoins },
      { id: "webhook", label: "Webhook Discord", icon: Webhook },
      { id: "bot-annunci", label: "Bot Discord", icon: Megaphone },
      { id: "link-discord", label: "Link Discord", icon: Link2 },
      { id: "countdown", label: "Countdown OBS", icon: Timer },
    ],
  },
];

const headers = () => ({ "X-Admin-Key": localStorage.getItem("admin_key") || "" });

const err403 = (err) => err?.response?.status === 403;

function Field({ label, id, value, onChange, placeholder = "", type = "text", textarea = false, required = false, hint = "" }) {
  const cls = "w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300";
  return (
    <div>
      <label className="hud-label block mb-2" htmlFor={id}>{label}</label>
      {hint && <p className="font-mono text-[10px] text-[#4A4D51] mb-2 leading-relaxed">{hint}</p>}
      {textarea ? (
        <textarea id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} rows={3} className={cls} data-testid={`${id}-input`} />
      ) : (
        <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className={cls} data-testid={`${id}-input`} />
      )}
    </div>
  );
}

function SaveButton({ label, busy, testid, onClick }) {
  return (
    <button type={onClick ? "button" : "submit"} onClick={onClick} disabled={busy} data-testid={testid} className="chamfer-sm bg-wardogs hover:bg-wardogs-hover disabled:opacity-50 text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300 flex items-center gap-2">
      <Plus className="w-4 h-4" /> {label}
    </button>
  );
}

function Box({ title, children, testid }) {
  return (
    <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 mb-8" data-testid={testid}>
      {title && <p className="hud-label mb-5">{title}</p>}
      {children}
    </div>
  );
}

function RowItem({ testid, title, sub, onRemove }) {
  return (
    <div className="bg-[#050505] p-5 flex items-center gap-4" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <p className="font-display text-xl uppercase truncate">{title}</p>
        {sub && <p className="font-mono text-[10px] text-steel truncate">{sub}</p>}
      </div>
      {onRemove && (
        <button onClick={onRemove} data-testid={`${testid}-remove`} className="text-steel hover:text-valkyra transition-colors duration-300" aria-label="Rimuovi">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({ twitch_login: "", display_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/streamers").then(({ data }) => setPartners(data.partners)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/streamers", form, { headers: headers() });
      toast.success("Partner aggiunto alla squadriglia.");
      setForm({ twitch_login: "", display_name: "", note: "" });
      load();
    } catch (err) {
      toast.error(err403(err) ? "Chiave admin non valida." : "Errore durante l'aggiunta.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (login) => {
    try {
      await api.delete(`/streamers/${login}`, { headers: headers() });
      toast.success(`Partner ${login} rimosso.`);
      load();
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };

  return (
    <>
      <Box title="Aggiungi streamer partner" testid="admin-add-partner-form">
        <form onSubmit={add}>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Field label="Nome canale Twitch" id="partner-login" value={form.twitch_login} onChange={(v) => setForm({ ...form, twitch_login: v })} required placeholder="es. DennyWhiteWolf" />
            <Field label="Nome da mostrare" id="partner-name" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} required placeholder="es. Denny White Wolf" />
          </div>
          <div className="mb-5">
            <Field label="Nota (opzionale)" id="partner-note" value={form.note} onChange={(v) => setForm({ ...form, note: v })} placeholder="es. Live il martedì e giovedì sera" />
          </div>
          <SaveButton label="Aggiungi partner" busy={busy} testid="admin-add-partner-button" />
        </form>
      </Box>
      <p className="hud-label mb-4">Partner attivi ({partners.length})</p>
      {partners.length === 0 ? (
        <Box testid="admin-partners-empty">
          <div className="flex items-center gap-4"><ShieldAlert className="w-5 h-5 text-steel" /><span className="font-mono text-xs uppercase tracking-[0.2em] text-steel">Nessun partner inserito</span></div>
        </Box>
      ) : (
        <div className="flex flex-col gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
          {partners.map((p) => (
            <RowItem key={p.twitch_login} testid={`admin-partner-${p.twitch_login}`} title={p.display_name} sub={`twitch.tv/${p.twitch_login}${p.note ? ` — ${p.note}` : ""}`} onRemove={() => remove(p.twitch_login)} />
          ))}
        </div>
      )}
    </>
  );
}

function GiveawayTab() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: "", prize: "", description: "", scheduled_at: "" });
  const [busy, setBusy] = useState(false);
  const [drawState, setDrawState] = useState(null);
  const [manualName, setManualName] = useState("");
  const load = () => api.get("/giveaways").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/giveaways", { ...form, scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : "" }, { headers: headers() });
      const discordNote = data.discord_announced ? " Annunciato su Discord con tag al ruolo Giveaway." : " Attenzione: annuncio Discord non riuscito.";
      toast.success((form.scheduled_at ? "Giveaway programmato: le iscrizioni si apriranno in automatico." : "Nuovo giveaway creato e aperto (il precedente è stato chiuso).") + discordNote);
      setForm({ title: "", prize: "", description: "", scheduled_at: "" });
      load();
    } catch (err) {
      toast.error(err403(err) ? "Chiave admin non valida." : "Errore nella creazione.");
    } finally {
      setBusy(false);
    }
  };
  const activate = async (id) => {
    try {
      await api.post(`/giveaways/${id}/activate`, {}, { headers: headers() });
      toast.success("Iscrizioni aperte in anticipo.");
      load();
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };
  const draw = async (id) => {
    const names = (data?.entries_list || []).map((e) => e.login);
    if (!names.length) {
      toast.error("Nessun partecipante da estrarre.");
      return;
    }
    if (drawState) return;
    let res;
    try {
      ({ data: res } = await api.post(`/giveaways/${id}/draw`, {}, { headers: headers() }));
    } catch (err) {
      toast.error(err?.response?.data?.detail === "no_entries" ? "Nessun partecipante da estrarre." : err403(err) ? "Chiave admin non valida." : "Errore nell'estrazione.");
      return;
    }
    // lo spettacolo (ruota, suoni, celebrazione) e' solo sul sito pubblico e su OBS:
    // qui nello Staff solo lo stato testuale, senza audio
    setDrawState({ winner: res.winner, done: false });
    setTimeout(() => { setDrawState((s) => (s ? { ...s, done: true } : s)); load(); }, 37000);
  };
  const seedTest = async (id) => {
    try {
      const { data: res } = await api.post(`/admin/giveaways/${id}/test-entries?count=8`, {}, { headers: headers() });
      toast.success(`${res.added} iscritti di prova aggiunti (totale ${res.total}).`);
      load();
    } catch (err) {
      toast.error(err403(err) ? "Chiave admin non valida." : "Errore nell'aggiunta degli iscritti di prova.");
    }
  };

  const addManual = async () => {
    const login = manualName.trim();
    if (!login || !data?.active || drawState) return;
    try {
      const { data: res } = await api.post(`/admin/giveaways/${data.active.id}/entries`, { login }, { headers: headers() });
      toast.success(res.already ? `${res.login} era gia' iscritto/a.` : `${res.login} aggiunto/a al giveaway (anche senza sub).`);
      setManualName("");
      load();
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(det === "utente_twitch_non_trovato" ? "Nessun account Twitch con questo nome — controlla il nome utente."
        : det === "nome_non_valido" ? "Nome non valido: usa il nome utente Twitch (lettere, numeri, _)."
        : det === "drawing_in_progress" ? "Estrazione in corso: impossibile aggiungere ora."
        : err403(err) ? "Chiave admin non valida." : "Aggiunta non riuscita.");
    }
  };

  const removeEntry = async (twitchId, login) => {
    if (!data?.active || drawState) return;
    try {
      await api.delete(`/admin/giveaways/${data.active.id}/entries/${twitchId}`, { headers: headers() });
      toast.success(`${login} rimosso/a dal giveaway.`);
      load();
    } catch {
      toast.error("Rimozione non riuscita.");
    }
  };

  const toggleOpen = async () => {
    if (!data?.active) return;
    const next = data.active.open === false;
    try {
      await api.post(`/admin/giveaways/${data.active.id}/toggle-open?open=${next}`, {}, { headers: headers() });
      toast.success(next
        ? "Iscrizioni aperte subito — la data programmata è stata annullata."
        : "Iscrizioni chiuse. Se c'era una data programmata futura, la riapertura automatica resta attiva.");
      load();
    } catch {
      toast.error("Operazione non riuscita.");
    }
  };

  const close = async (id) => {
    try {
      await api.post(`/giveaways/${id}/close`, {}, { headers: headers() });
      toast.success("Giveaway chiuso senza estrazione.");
      load();
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };

  return (
    <>
      {drawState && !drawState.done && (
        <div className="chamfer border border-wardogs/40 bg-[#050505] p-8 mb-8 text-center relative overflow-hidden scanlines-bg" data-testid="draw-animation">
          <div className="absolute top-0 left-0 right-0 h-px bg-wardogs" />
          <p className="hud-label mb-3">Estrazione in corso</p>
          <p className="font-display text-3xl uppercase text-wardogs leading-tight">La ruota sta girando sul sito pubblico</p>
          <p className="font-mono text-[11px] text-steel mt-3">30 secondi di giro + 6 di celebrazione, con suoni e coriandoli sulla pagina Giveaway e sull'overlay OBS. Qui niente spettacolo: guardala dal vivo!</p>
        </div>
      )}
      {drawState?.done && (
        <div className="chamfer border border-manticore/50 bg-[#050505] p-10 mb-8 text-center relative overflow-hidden scanlines-bg" data-testid="draw-animation">
          <div className="absolute top-0 left-0 right-0 h-px bg-manticore" />
          <p className="hud-label mb-4">Vincitore estratto</p>
          <p className="font-display text-6xl md:text-7xl uppercase leading-none text-manticore" data-testid="draw-rolling-name">
            {drawState.winner}
          </p>
          <div className="mt-6 flex flex-col items-center gap-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-steel">Il giveaway si chiude in automatico — il vincitore entra nella lista dei giveaway passati</p>
            <button onClick={() => { setDrawState(null); load(); }} data-testid="draw-dismiss-button" className="chamfer-sm border border-[#2A2A2A] hover:border-white text-steel hover:text-white font-mono text-[10px] uppercase tracking-[0.2em] px-6 py-3 transition-colors duration-300">
              Chiudi
            </button>
          </div>
        </div>
      )}
      {data?.active && (
        <Box title="Giveaway attivo" testid="admin-giveaway-active">
          <p className="font-display text-2xl uppercase mb-1">{data.active.title}</p>
          <p className="font-mono text-xs text-steel mb-3">{data.active.prize} — {data.entries} partecipanti</p>
          {data.active.open === false ? (
            <div className="chamfer-sm border border-wardogs/40 bg-wardogs-dim px-4 py-3 mb-5 flex flex-wrap items-center gap-4" data-testid="admin-giveaway-scheduled">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-wardogs flex-1">
                Iscrizioni sigillate{data.active.scheduled_at ? ` — apertura automatica il ${new Date(data.active.scheduled_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
              </p>
              <button onClick={() => activate(data.active.id)} data-testid="admin-giveaway-activate-button" className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300">
                Apri ora
              </button>
            </div>
          ) : (
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-manticore mb-5" data-testid="admin-giveaway-open">Iscrizioni aperte</p>
          )}
          <div className="flex gap-2 mb-2">
            <input
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
              placeholder="Nome Twitch della persona (senza @)…"
              data-testid="admin-giveaway-manual-input"
              className="flex-1 bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300"
            />
            <button onClick={addManual} disabled={!!drawState || !manualName.trim()} data-testid="admin-giveaway-manual-add"
              className="chamfer-sm bg-wardogs hover:bg-wardogs-hover disabled:opacity-50 text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3 transition-colors duration-300">
              Aggiungi persona
            </button>
          </div>
          <p className="font-mono text-[10px] text-[#4A4D51] mb-5 leading-relaxed">Iscrizione manuale dello Staff: la persona entra nella ruota anche senza abbonamento. Il nome viene verificato su Twitch.</p>
          {(data.entries_list || []).length > 0 && (
            <div className="flex flex-col gap-px bg-[#1A1A1A] border border-[#1A1A1A] mb-5" data-testid="admin-giveaway-entries">
              {data.entries_list.map((e, i) => (
                <div key={`${e.login}-${i}`} className="bg-[#050505] px-4 py-3 flex items-center gap-4" data-testid={`admin-entry-${e.login}`}>
                  <span className="font-mono text-[10px] text-[#4A4D51] w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-mono text-xs text-white flex-1 truncate">{e.login}</span>
                  {e.tier && <span className="font-mono text-[10px] text-[#9146FF]">T{String(e.tier)[0]}</span>}
                  {e.manual && <span className="font-mono text-[10px] text-wardogs" title="Aggiunta manualmente dallo Staff">STAFF</span>}
                  <span className="font-mono text-[10px] text-steel">{new Date(e.entered_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  <button onClick={() => removeEntry(e.twitch_id, e.login)} disabled={!!drawState} data-testid={`admin-entry-remove-${e.login}`} title="Rimuovi dal giveaway"
                    className="text-[#4A4D51] hover:text-valkyra transition-colors duration-200 disabled:opacity-40">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <button onClick={() => draw(data.active.id)} disabled={!!drawState} data-testid="admin-giveaway-draw-button" className="chamfer-sm bg-manticore hover:bg-[#2aa74b] disabled:opacity-50 text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3 transition-colors duration-300">
              Estrai vincitore e chiudi
            </button>
            <button onClick={toggleOpen} disabled={!!drawState} data-testid="admin-giveaway-toggle-open" className="chamfer-sm border border-[#2A2A2A] hover:border-white text-steel hover:text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3 transition-colors duration-300">
              {data.active.open !== false ? "Chiudi iscrizioni" : "Apri iscrizioni"}
            </button>
            <button onClick={() => seedTest(data.active.id)} disabled={!!drawState} data-testid="admin-giveaway-test-entries-button" className="chamfer-sm border border-[#2A2A2A] hover:border-wardogs text-steel hover:text-wardogs font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3 transition-colors duration-300">
              +8 iscritti di prova
            </button>
            <button onClick={() => close(data.active.id)} data-testid="admin-giveaway-close-button" className="chamfer-sm border border-valkyra text-valkyra hover:bg-valkyra hover:text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3 transition-colors duration-300">
              Chiudi senza estrazione
            </button>
          </div>
        </Box>
      )}
      <Box title="Crea nuovo giveaway" testid="admin-giveaway-create-form">
        <form onSubmit={create}>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Field label="Titolo" id="gv-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required placeholder="es. Chiave Steam WARDOGS" />
            <Field label="Premio" id="gv-prize" value={form.prize} onChange={(v) => setForm({ ...form, prize: v })} required placeholder="es. 1x Chiave Steam" />
          </div>
          <div className="mb-4">
            <Field label="Descrizione" id="gv-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea placeholder="Dettagli del giveaway…" />
          </div>
          <div className="mb-5">
            <Field label="Apertura iscrizioni (opzionale — lascia vuoto per aprire subito)" id="gv-start" type="datetime-local" value={form.scheduled_at} onChange={(v) => setForm({ ...form, scheduled_at: v })} />
            <p className="font-mono text-[10px] text-steel mt-2">Se imposti data e ora, il giveaway apparirà sul sito con un countdown e le iscrizioni si apriranno da sole allo scadere.</p>
          </div>
          <SaveButton label="Crea giveaway" busy={busy} testid="admin-giveaway-create-button" />
        </form>
      </Box>
      {(data?.past || []).length > 0 && (
        <>
          <p className="hud-label mb-4">Storico</p>
          <div className="flex flex-col gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
            {data.past.map((g) => (
              <div key={g.id} className="bg-[#050505] px-4 py-3.5 flex items-center gap-3" data-testid={`admin-giveaway-past-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-xs text-white truncate">{g.title}</p>
                  <p className="font-mono text-[10px] text-steel">{g.winner ? `Vincitore: ${g.winner.login}` : "Chiuso senza estrazione"}</p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Ripristinare "${g.title}" come giveaway attivo? Il vincitore viene azzerato, gli iscritti restano e la ruota puo' girare di nuovo.`)) return;
                    try {
                      await api.post(`/admin/giveaways/${g.id}/restore`, {}, { headers: headers() });
                      toast.success("Giveaway ripristinato come attivo.");
                      load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail === "another_active" ? "Chiudi prima il giveaway attivo." : "Ripristino non riuscito.");
                    }
                  }}
                  className="text-[#4A4D51] hover:text-manticore transition-colors duration-300 shrink-0"
                  data-testid={`admin-giveaway-restore-${g.id}`}
                  title="Ripristina come giveaway attivo"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Eliminare definitivamente il giveaway "${g.title}" e tutte le sue iscrizioni?`)) return;
                    try {
                      await api.delete(`/giveaways/${g.id}`, { headers: headers() });
                      toast.success("Giveaway eliminato.");
                      load();
                    } catch {
                      toast.error("Eliminazione non riuscita.");
                    }
                  }}
                  className="text-[#4A4D51] hover:text-valkyra transition-colors duration-300 shrink-0"
                  data-testid={`admin-giveaway-delete-${g.id}`}
                  title="Elimina definitivamente"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

const BOT_COLORS = [
  { hex: "#FF5C00", label: "Arancione" },
  { hex: "#9146FF", label: "Viola" },
  { hex: "#5865F2", label: "Blu" },
  { hex: "#34C759", label: "Verde" },
  { hex: "#FF3B30", label: "Rosso" },
];

const BOT_EMPTY = {
  channel_id: "", channel_name: "", content: "", mention: "none",
  embedOn: false, embTitle: "", embDesc: "", embUrl: "", embColor: "#FF5C00", embImage: "",
  linkOn: false, linkLabel: "", linkUrl: "", mode: "now", schedule: "", repeat: "none",
};

// Editor testo con toolbar markdown Discord: grassetto, corsivo, sottolineato, barrato,
// spoiler, titolo, citazione, elenco puntato e link cliccabile. Agisce sulla selezione.
function MdEditor({ value, onChange, rows = 5, testid, placeholder = "", maxLength = 4000 }) {
  const ref = useRef(null);
  const wrap = (before, after = before) => {
    const ta = ref.current;
    if (!ta) return;
    const s = ta.selectionStart ?? value.length;
    const e = ta.selectionEnd ?? value.length;
    const sel = value.slice(s, e);
    onChange(value.slice(0, s) + before + sel + after + value.slice(e));
    requestAnimationFrame(() => {
      ta.focus();
      if (sel) ta.setSelectionRange(s + before.length, s + before.length + sel.length);
      else { const p = s + before.length; ta.setSelectionRange(p, p); }
    });
  };
  const linePrefix = (prefix) => {
    const ta = ref.current;
    if (!ta) return;
    const s = ta.selectionStart ?? value.length;
    const e = ta.selectionEnd ?? value.length;
    const ls = value.lastIndexOf("\n", Math.max(0, s - 1)) + 1;
    const seg = value.slice(ls, e);
    const allOn = seg.split("\n").filter((l) => l.trim()).every((l) => l.startsWith(prefix));
    const patched = seg.split("\n").map((l) => {
      if (!l.trim()) return l;
      if (allOn && l.startsWith(prefix)) return l.slice(prefix.length);
      return l.startsWith(prefix) ? l : prefix + l;
    }).join("\n");
    onChange(value.slice(0, ls) + patched + value.slice(e));
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(ls, ls + patched.length); });
  };
  const insertLink = () => {
    const ta = ref.current;
    const url = window.prompt("Indirizzo del link:", "https://");
    if (!url || !url.startsWith("http")) return;
    const s = ta?.selectionStart ?? value.length;
    const e = ta?.selectionEnd ?? value.length;
    const sel = value.slice(s, e) || "testo del link";
    onChange(value.slice(0, s) + `[${sel}](${url})` + value.slice(e));
  };
  const tools = [
    { icon: Bold, title: "Grassetto", act: () => wrap("**"), tid: "bold" },
    { icon: Italic, title: "Corsivo", act: () => wrap("*"), tid: "italic" },
    { icon: Underline, title: "Sottolineato", act: () => wrap("__"), tid: "underline" },
    { icon: Strikethrough, title: "Barrato", act: () => wrap("~~"), tid: "strike" },
    { icon: EyeOff, title: "Spoiler (testo nascosto)", act: () => wrap("||"), tid: "spoiler" },
    { icon: Heading1, title: "Titolo di sezione", act: () => linePrefix("# "), tid: "heading" },
    { icon: Quote, title: "Citazione", act: () => linePrefix("> "), tid: "quote" },
    { icon: List, title: "Elenco puntato", act: () => linePrefix("- "), tid: "list" },
    { icon: Link2, title: "Link cliccabile", act: insertLink, tid: "link" },
  ];
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-1.5" data-testid={`${testid}-toolbar`}>
        {tools.map((t) => (
          <button key={t.tid} type="button" title={t.title} onClick={t.act} data-testid={`${testid}-fmt-${t.tid}`}
            className="w-8 h-8 flex items-center justify-center chamfer-sm border border-[#1A1A1A] text-steel hover:text-wardogs hover:border-wardogs transition-colors duration-200">
            <t.icon size={13} strokeWidth={2.2} />
          </button>
        ))}
      </div>
      <textarea ref={ref} value={value} onChange={(e) => onChange(e.target.value)} rows={rows} maxLength={maxLength}
        placeholder={placeholder} data-testid={testid}
        className="w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-3 py-2 font-mono text-xs text-white transition-colors duration-300" />
    </div>
  );
}

// Mini-renderer markdown Discord per l'anteprima live (grassetto, corsivo, sottolineato,
// barrato, spoiler, link, URL nudi, #titoli, >citazioni, -elenchi, #canali, @menzioni).
const MD_RE = /(\*\*([\s\S]+?)\*\*)|(__([\s\S]+?)__)|(~~([\s\S]+?)~~)|(\|\|([\s\S]+?)\|\|)|(\*([^*\n]+?)\*)|(_([^_\n]+?)_)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(https?:\/\/[^\s<)]+[^\s<).,;:!?])|(<#(\d+)>)|(@everyone|@here)/;

function mdNodes(text, channels, depth = 0, keyBase = "k") {
  if (!text || depth > 8) return text;
  const out = [];
  let rest = text, i = 0;
  while (rest) {
    const m = rest.match(MD_RE);
    if (!m || m.index === undefined) { out.push(rest); break; }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const key = `${keyBase}-${i}`;
    if (m[2] !== undefined) out.push(<strong key={key} className="font-bold text-white">{mdNodes(m[2], channels, depth + 1, key)}</strong>);
    else if (m[4] !== undefined) out.push(<span key={key} className="underline underline-offset-2">{mdNodes(m[4], channels, depth + 1, key)}</span>);
    else if (m[6] !== undefined) out.push(<s key={key}>{mdNodes(m[6], channels, depth + 1, key)}</s>);
    else if (m[8] !== undefined) out.push(<span key={key} title="Spoiler" className="bg-[#1E1F22] text-transparent hover:text-[#DBDEE1] rounded-sm px-0.5 cursor-pointer transition-colors duration-150">{mdNodes(m[8], channels, depth + 1, key)}</span>);
    else if (m[10] !== undefined) out.push(<em key={key}>{m[10]}</em>);
    else if (m[12] !== undefined) out.push(<em key={key}>{m[12]}</em>);
    else if (m[14] !== undefined) out.push(<a key={key} href={m[15]} target="_blank" rel="noreferrer" className="text-[#00A8FC] hover:underline">{m[14]}</a>);
    else if (m[16] !== undefined) out.push(<a key={key} href={m[16]} target="_blank" rel="noreferrer" className="text-[#00A8FC] hover:underline break-all">{m[16]}</a>);
    else if (m[18] !== undefined) {
      const ch = (channels || []).find((c) => c.id === m[18]);
      out.push(<span key={key} className="text-[#C9CDFB] bg-[#3C4270]/50 rounded-sm px-0.5 font-medium">#{ch ? ch.name : "canale"}</span>);
    } else out.push(<span key={key} className="text-[#C9CDFB] bg-[#3C4270]/50 rounded-sm px-0.5 font-medium">{m[20]}</span>);
    rest = rest.slice(m.index + m[0].length);
    i += 1;
  }
  return out;
}

function DiscordMarkdown({ text, channels, className = "" }) {
  const lines = String(text || "").split("\n");
  return (
    <div className={className}>
      {lines.map((l, i) => {
        if (l.startsWith("# ")) return <p key={i} className="text-white font-bold text-base leading-snug mt-1 first:mt-0">{mdNodes(l.slice(2), channels, 0, `h${i}`)}</p>;
        if (l.startsWith("> ")) return <p key={i} className="border-l-2 border-[#4E5058] pl-2 my-0.5">{mdNodes(l.slice(2), channels, 0, `q${i}`)}</p>;
        if (l.startsWith("- ")) return <p key={i} className="pl-3 relative"><span className="absolute left-0.5">•</span>{mdNodes(l.slice(2), channels, 0, `b${i}`)}</p>;
        return <p key={i} className="min-h-[1.1em] break-words">{mdNodes(l, channels, 0, `l${i}`)}</p>;
      })}
    </div>
  );
}

// Anteprima stile Discord condivisa: menzione, testo con markdown, card embed, pulsante link.
function DiscordPreview({ mention = "none", mentionLabel = "", content = "", embed = {}, link = {}, channels = [], testid }) {
  const hasEmbed = embed.enabled && (embed.title || embed.description);
  return (
    <div className="bg-[#313338] p-4 font-sans text-left rounded-sm" data-testid={testid}>
      {mention !== "none" && <p className="text-[#949CF7] text-sm font-medium mb-1">{mentionLabel || (mention === "everyone" ? "@everyone" : "@here")}</p>}
      {content && <DiscordMarkdown text={content} channels={channels} className="text-[#DBDEE1] text-sm" />}
      {hasEmbed && (
        <div className="mt-2 bg-[#2B2D31] p-3 rounded-sm" style={{ borderLeft: `4px solid ${embed.color || "#FF5C00"}` }}>
          {embed.title && <p className="text-white font-semibold text-sm">{embed.title}</p>}
          {embed.description && <DiscordMarkdown text={embed.description} channels={channels} className="text-[#DBDEE1] text-xs mt-1" />}
          {embed.image_url && <img src={embed.image_url} alt="" className="mt-2 max-h-32 rounded-sm" onError={(e) => { e.target.style.display = "none"; }} />}
        </div>
      )}
      {link.enabled && link.url && (
        <button className="mt-2 bg-[#4E5058] text-white text-xs font-medium px-3 py-1.5 rounded">{link.label || "Apri"}</button>
      )}
      {!content && !hasEmbed && (
        <p className="text-[#6D6F78] text-sm italic">Scrivi qualcosa per vedere l'anteprima…</p>
      )}
    </div>
  );
}


// Messaggi gestiti dal bot: statici (testo + card embed + pulsante link, es. PayPal),
// classifica auto-aggiornata (intestazione/pie' di pagina editabili) e modello DM benvenuto.
// Se un messaggio e' stato cancellato su Discord, salvando il bot lo ricrea nel canale originale.
function ManagedMessagesBox() {
  const [msgs, setMsgs] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const inpCls = "w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-3 py-2 font-mono text-xs text-white transition-colors duration-300";
  const load = () => api.get("/admin/discord-messages", { headers: headers() })
    .then(({ data }) => {
      const list = data.messages || [];
      setMsgs(list);
      setDrafts(Object.fromEntries(list.map((m) => [m.key, {
        content: m.content || "",
        embOn: !!m.embed?.enabled,
        embTitle: m.embed?.title || "",
        embDesc: m.embed?.description || "",
        embColor: m.embed?.color || "#FF5C00",
        embImage: m.embed?.image_url || "",
        linkOn: !!m.link_button?.enabled,
        linkLabel: m.link_button?.label || "",
        linkUrl: m.link_button?.url || "",
        intro: m.intro || "",
        outro: m.outro || "",
      }])));
    }).catch(() => {});
  useEffect(() => { load(); }, []);
  const setD = (key, k) => (v) => setDrafts((p) => ({ ...p, [key]: { ...(p[key] || {}), [k]: v } }));
  const save = async (key) => {
    if (busyKey) return;
    setBusyKey(key);
    const d = drafts[key] || {};
    const kind = (msgs.find((x) => x.key === key) || {}).kind || "static";
    const body = kind === "leaderboard"
      ? { intro: d.intro || "", outro: d.outro || "" }
      : kind === "dm"
        ? { content: d.content || "" }
        : { content: d.content || "",
            embed: { enabled: !!d.embOn, title: d.embTitle || "", description: d.embDesc || "", color: d.embColor || "#FF5C00", image_url: d.embImage || "" },
            link_button: { enabled: !!d.linkOn, label: d.linkLabel || "", url: d.linkUrl || "" } };
    try {
      const { data } = await api.post(`/admin/discord-messages/${key}`, body, { headers: headers() });
      toast.success(kind === "dm"
        ? "Modello di benvenuto salvato: varra' per i prossimi nuovi membri."
        : data.recreated
          ? "Messaggio ricreato su Discord (quello vecchio era stato cancellato)."
          : "Messaggio aggiornato su Discord.");
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det === "classifica_vuota" ? "Classifica ancora vuota: riprova quando ci sono giocatori."
        : det === "link_non_valido" ? "Il link del pulsante deve iniziare con http."
        : det === "discord_update_failed" ? "Discord non ha accettato l'aggiornamento: controlla che il bot sia operativo e riprova."
        : det || "Aggiornamento non riuscito — controlla la chiave admin.");
    } finally {
      setBusyKey("");
    }
  };
  const testDm = async () => {
    if (busyKey) return;
    setBusyKey("welcome-test");
    try {
      await api.post("/admin/discord-messages/welcome/test", {}, { headers: headers() });
      toast.success("DM di prova inviato: controlla i messaggi privati su Discord.");
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det === "discord_non_collegato" ? "Collega il tuo account Discord dal profilo per ricevere il test."
        : det === "dm_non_consegnato" ? "Discord non consegna: abilita i DM dai membri del server (Impostazioni → Privacy) e riprova."
        : "Accedi con il tuo account staff per ricevere il DM di prova.");
    } finally {
      setBusyKey("");
    }
  };
  if (!msgs || !msgs.length) return null;
  return (
    <Box title="Messaggi fissi del bot — modifica e ripubblica" testid="admin-managed-messages">
      <p className="font-mono text-[11px] text-steel leading-relaxed mb-4">
        Questi messaggi vivono fissi nei canali (o nei DM di benvenuto). Modifica testo, card grafica e pulsante e salva: il bot aggiorna il messaggio su Discord; se e' stato cancellato, lo ricrea automaticamente nel canale originale.
      </p>
      <div className="space-y-5">
        {msgs.map((m) => {
          const d = drafts[m.key] || {};
          const kind = m.kind || "static";
          return (
            <div key={m.key} className="border border-[#1A1A1A] chamfer-sm p-4" data-testid={`managed-msg-${m.key}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-wardogs mb-3">
                {m.label}{" "}
                {kind === "dm"
                  ? <span className="text-manticore" data-testid={`managed-msg-status-${m.key}`}>— modello attivo per i prossimi membri</span>
                  : m.exists
                    ? <span className="text-manticore" data-testid={`managed-msg-status-${m.key}`}>— attivo su Discord</span>
                    : <span className="text-red-500" data-testid={`managed-msg-status-${m.key}`}>— non trovato su Discord: verra' ricreato al salvataggio</span>}
              </p>
              <div className="grid lg:grid-cols-2 gap-4 items-start">
                <div className="flex flex-col gap-3">
                  {kind === "leaderboard" && (
                    <>
                      <p className="font-mono text-[10px] text-[#4A4D51] leading-relaxed -mt-1">La tabella della classifica si aggiorna da sola ogni minuto: qui modifichi solo intestazione e pie' di pagina.</p>
                      <div>
                        <label className="hud-label block mb-2">Intestazione</label>
                        <MdEditor value={d.intro ?? ""} onChange={setD(m.key, "intro")} rows={2} maxLength={200}
                          testid={`managed-msg-intro-${m.key}`} placeholder="Titolo del messaggio classifica…" />
                      </div>
                      <div>
                        <label className="hud-label block mb-2">Pie' di pagina</label>
                        <MdEditor value={d.outro ?? ""} onChange={setD(m.key, "outro")} rows={2} maxLength={300}
                          testid={`managed-msg-outro-${m.key}`} placeholder="Riga sotto la classifica…" />
                      </div>
                    </>
                  )}
                  {kind !== "leaderboard" && (
                    <div>
                      <label className="hud-label block mb-2">{kind === "dm" ? "Testo del messaggio di benvenuto" : "Testo del messaggio"}</label>
                      {kind === "dm" && <p className="font-mono text-[10px] text-[#4A4D51] mb-2 leading-relaxed">Scrivi {"{name}"} dove vuoi il nome del nuovo membro. I riferimenti #canale restano cliccabili nel DM.</p>}
                      <MdEditor value={d.content ?? ""} onChange={setD(m.key, "content")} rows={kind === "dm" ? 8 : 4} maxLength={2000}
                        testid={`managed-msg-textarea-${m.key}`} placeholder={kind === "dm" ? "Ciao **{name}**, benvenuto/a…" : "Testo sopra la card (facoltativo)…"} />
                    </div>
                  )}
                  {kind === "static" && (
                    <>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!d.embOn} onChange={(e) => setD(m.key, "embOn")(e.target.checked)} className="accent-[#FF5C00]" data-testid={`managed-msg-embed-toggle-${m.key}`} />
                        <span className="hud-label">Card grafica (embed)</span>
                      </label>
                      {d.embOn && (
                        <div className="flex flex-col gap-3 border border-[#1A1A1A] chamfer-sm p-3">
                          <div>
                            <label className="hud-label block mb-2">Titolo card</label>
                            <input value={d.embTitle ?? ""} onChange={(e) => setD(m.key, "embTitle")(e.target.value)} maxLength={256}
                              placeholder="Es. SOSTIENI LA COMMUNITY" data-testid={`managed-msg-emb-title-${m.key}`} className={inpCls} />
                          </div>
                          <div>
                            <label className="hud-label block mb-2">Testo della card</label>
                            <MdEditor value={d.embDesc ?? ""} onChange={setD(m.key, "embDesc")} rows={5} maxLength={4000}
                              testid={`managed-msg-emb-desc-${m.key}`} placeholder="Descrizione ricca del messaggio…" />
                          </div>
                          <div>
                            <label className="hud-label block mb-2">Colore bordo</label>
                            <div className="flex gap-2 flex-wrap">
                              {BOT_COLORS.map((c) => (
                                <button key={c.hex} type="button" title={c.label} onClick={() => setD(m.key, "embColor")(c.hex)} data-testid={`managed-msg-color-${m.key}-${c.hex.slice(1)}`}
                                  className={`w-8 h-8 chamfer-sm border-2 transition-all duration-200 ${d.embColor === c.hex ? "border-white scale-110" : "border-transparent"}`}
                                  style={{ background: c.hex }} />
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="hud-label block mb-2">Immagine card — URL (facoltativo)</label>
                            <input value={d.embImage ?? ""} onChange={(e) => setD(m.key, "embImage")(e.target.value)}
                              placeholder="https://…/immagine.png" data-testid={`managed-msg-emb-image-${m.key}`} className={inpCls} />
                          </div>
                        </div>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!d.linkOn} onChange={(e) => setD(m.key, "linkOn")(e.target.checked)} className="accent-[#FF5C00]" data-testid={`managed-msg-link-toggle-${m.key}`} />
                        <span className="hud-label">Pulsante link sotto il messaggio</span>
                      </label>
                      {d.linkOn && (
                        <div className="grid sm:grid-cols-2 gap-3 border border-[#1A1A1A] chamfer-sm p-3">
                          <div>
                            <label className="hud-label block mb-2">Testo del pulsante</label>
                            <input value={d.linkLabel ?? ""} onChange={(e) => setD(m.key, "linkLabel")(e.target.value)} maxLength={80}
                              placeholder="Es. DONA ORA" data-testid={`managed-msg-link-label-${m.key}`} className={inpCls} />
                          </div>
                          <div>
                            <label className="hud-label block mb-2">Indirizzo del link</label>
                            <input value={d.linkUrl ?? ""} onChange={(e) => setD(m.key, "linkUrl")(e.target.value)}
                              placeholder="https://…" data-testid={`managed-msg-link-url-${m.key}`} className={inpCls} />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => save(m.key)} disabled={busyKey === m.key} data-testid={`managed-msg-save-${m.key}`}
                      className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300 disabled:opacity-50">
                      {busyKey === m.key ? "..." : kind === "dm" ? "Salva modello" : (m.exists ? "Salva e ripubblica" : "Ricrea su Discord")}
                    </button>
                    {kind === "dm" && (
                      <button onClick={testDm} disabled={busyKey === "welcome-test"} data-testid="managed-msg-test-welcome"
                        className="chamfer-sm border border-manticore text-manticore hover:bg-manticore hover:text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300 disabled:opacity-50">
                        {busyKey === "welcome-test" ? "..." : "Inviami un test"}
                      </button>
                    )}
                  </div>
                </div>
                <DiscordPreview
                  content={kind === "leaderboard"
                    ? `${d.intro || ""}\n[ tabella classifica — aggiornata automaticamente ogni minuto ]\n${d.outro || ""}`
                    : kind === "dm"
                      ? (d.content || "").split("{name}").join("Soldato")
                      : (d.content || "")}
                  embed={kind === "static" ? { enabled: !!d.embOn, title: d.embTitle || "", description: d.embDesc || "", color: d.embColor || "#FF5C00", image_url: d.embImage || "" } : {}}
                  link={kind === "static" ? { enabled: !!d.linkOn, label: d.linkLabel || "", url: d.linkUrl || "" } : {}}
                  testid={`managed-msg-preview-${m.key}`} />
              </div>
            </div>
          );
        })}
      </div>
    </Box>
  );
}


// Tab "Notifica Live": modello personalizzabile dell'annuncio di inizio live su Discord
// (testo, menzione, card embed, immagine, pulsante link). Salvato nelle impostazioni del sito,
// usato dal watchdog quando il canale Twitch va in diretta.
function LiveNotifyTab() {
  const SAMPLE = { title: "DOMINIO SU WARDOGS — SI GIOCA LA SEASON 1", url: "https://www.twitch.tv/ilquartiermastro", channel: "ilquartiermastro", game: "WARDOGS" };
  const [cfg, setCfg] = useState(null);
  const [hasWebhook, setHasWebhook] = useState(true);
  const [busy, setBusy] = useState("");
  const inpCls = "w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-3 py-2 font-mono text-xs text-white transition-colors duration-300";
  const selCls = "w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300";
  const load = () => api.get("/admin/live-notify", { headers: headers() })
    .then(({ data }) => { setCfg(data.config); setHasWebhook(!!data.has_webhook); })
    .catch((e) => toast.error(err403(e) ? "Permesso Notifica Live non assegnato (sezione Staff)." : "Modello non caricato."));
  useEffect(() => { load(); }, []);
  if (!cfg) return null;
  const setE = (k) => (v) => setCfg((p) => ({ ...p, embed: { ...p.embed, [k]: v } }));
  const setL = (k) => (v) => setCfg((p) => ({ ...p, link_button: { ...p.link_button, [k]: v } }));
  const fill = (t) => (t || "").split("{title}").join(SAMPLE.title).split("{url}").join(SAMPLE.url).split("{channel}").join(SAMPLE.channel).split("{game}").join(SAMPLE.game);
  const save = async () => {
    if (busy) return;
    setBusy("save");
    try {
      await api.put("/admin/live-notify", cfg, { headers: headers() });
      toast.success("Modello salvato: il prossimo annuncio live usera' questa grafica.");
    } catch (e) {
      toast.error(e?.response?.data?.detail === "contenuto_non_valido" ? "Scrivi un testo o attiva la card embed." : "Salvataggio non riuscito — controlla la chiave admin.");
    } finally {
      setBusy("");
    }
  };
  const test = async () => {
    if (busy) return;
    setBusy("test");
    try {
      await api.post("/admin/live-notify/test", {}, { headers: headers() });
      toast.success("Annuncio di prova inviato nel canale live.");
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det === "no_webhook" ? "Configura prima il webhook nel tab Webhook Discord."
        : "Discord ha rifiutato il messaggio: controlla il webhook nel tab Webhook Discord.");
    } finally {
      setBusy("");
    }
  };
  return (
    <Box title="Notifica inizio live — modello dell'annuncio" testid="admin-live-notify">
      <p className="font-mono text-[11px] text-steel leading-relaxed mb-2">
        Quando il canale va in diretta su Twitch, il bot pubblica questo annuncio nel canale delle live. Personalizza testo, menzione, card grafica e pulsante: il modello vale da subito per tutte le prossime live.
      </p>
      <p className="font-mono text-[10px] text-[#4A4D51] leading-relaxed mb-4">
        Segnaposto: {"{title}"} = titolo della live · {"{url}"} = link Twitch · {"{channel}"} = nome canale · {"{game}"} = gioco
      </p>
      {!hasWebhook && (
        <div className="chamfer-sm border border-red-500/40 bg-red-500/10 px-3 py-2 mb-4" data-testid="live-notify-no-webhook">
          <p className="font-mono text-[11px] text-red-400">Nessun webhook configurato: impostalo nel tab "Webhook Discord" o gli annunci non partono.</p>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="flex flex-col gap-4">
          <div>
            <label className="hud-label block mb-2">Menzione</label>
            <select value={cfg.mention} onChange={(e) => setCfg((p) => ({ ...p, mention: e.target.value }))} className={selCls} data-testid="live-notify-mention">
              <option value="none">Nessuna</option>
              <option value="role">Ruolo ping (impostato nel tab Webhook Discord)</option>
              <option value="everyone">@everyone</option>
              <option value="here">@here</option>
            </select>
          </div>
          <div>
            <label className="hud-label block mb-2">Testo dell'annuncio</label>
            <MdEditor value={cfg.content} onChange={(v) => setCfg((p) => ({ ...p, content: v }))} rows={4} maxLength={2000} testid="live-notify-content-input" placeholder="**ILQUARTIERMASTRO È IN LIVE!**…" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!cfg.embed.enabled} onChange={(e) => setE("enabled")(e.target.checked)} className="accent-[#FF5C00]" data-testid="live-notify-embed-toggle" />
            <span className="hud-label">Card grafica (embed)</span>
          </label>
          {cfg.embed.enabled && (
            <div className="flex flex-col gap-3 border border-[#1A1A1A] chamfer-sm p-3">
              <div>
                <label className="hud-label block mb-2">Titolo card</label>
                <input value={cfg.embed.title} onChange={(e) => setE("title")(e.target.value)} maxLength={256} className={inpCls} data-testid="live-notify-emb-title" placeholder="{title}" />
              </div>
              <div>
                <label className="hud-label block mb-2">Testo della card</label>
                <MdEditor value={cfg.embed.description} onChange={setE("description")} rows={3} maxLength={4000} testid="live-notify-emb-desc-input" placeholder="Descrizione (facoltativa)…" />
              </div>
              <div>
                <label className="hud-label block mb-2">Colore bordo</label>
                <div className="flex gap-2 flex-wrap">
                  {BOT_COLORS.map((c) => (
                    <button key={c.hex} type="button" title={c.label} onClick={() => setE("color")(c.hex)} data-testid={`live-notify-color-${c.hex.slice(1)}`}
                      className={`w-8 h-8 chamfer-sm border-2 transition-all duration-200 ${cfg.embed.color === c.hex ? "border-white scale-110" : "border-transparent"}`}
                      style={{ background: c.hex }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="hud-label block mb-2">Immagine della card</label>
                <select value={cfg.embed.image} onChange={(e) => setE("image")(e.target.value)} className={selCls} data-testid="live-notify-image-mode">
                  <option value="thumb">Anteprima della live (automatica da Twitch)</option>
                  <option value="custom">Banner live del sito / immagine personalizzata</option>
                  <option value="none">Nessuna immagine</option>
                </select>
              </div>
              {cfg.embed.image === "custom" && (() => {
                const bp = (cfg.embed.image_url || "").match(/live-banner(-v2)?(-bar)?\.png(\?v=\d+)?$/);
                const preset = bp ? (bp[1] ? "v2" : "v1") : "custom";
                const bar = !!(bp && bp[2]);
                const setPreset = (v, b) => setE("image_url")(`${window.location.origin}/live-banner${v === "v2" ? "-v2" : ""}${b ? "-bar" : ""}.png?v=2`);
                return (
                  <>
                    <div>
                      <label className="hud-label block mb-2">Banner</label>
                      <select value={preset} onChange={(e) => { const v = e.target.value; if (v === "custom") setE("image_url")(""); else setPreset(v, bar); }} className={selCls} data-testid="live-notify-banner-preset">
                        <option value="v1">Soldato — posa 1 (braci al tramonto)</option>
                        <option value="v2">Soldato — posa 2 (operazione notturna)</option>
                        <option value="custom">URL personalizzato…</option>
                      </select>
                    </div>
                    {preset !== "custom" ? (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={bar} onChange={(e) => setPreset(preset, e.target.checked)} className="accent-[#FF5C00]" data-testid="live-notify-banner-bar" />
                          <span className="hud-label">Barra arancione laterale nel banner (Discord mostra gia' la sua: di solito spenta)</span>
                        </label>
                        <img src={cfg.embed.image_url} alt="Anteprima banner" className="w-full chamfer-sm border border-[#1A1A1A]" data-testid="live-notify-banner-thumb" />
                      </>
                    ) : (
                      <div>
                        <label className="hud-label block mb-2">Immagine — URL</label>
                        <input value={cfg.embed.image_url} onChange={(e) => setE("image_url")(e.target.value)} className={inpCls} data-testid="live-notify-image-url" placeholder="https://…/copertina.png" />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!cfg.link_button.enabled} onChange={(e) => setL("enabled")(e.target.checked)} className="accent-[#FF5C00]" data-testid="live-notify-link-toggle" />
            <span className="hud-label">Pulsante link sotto il messaggio</span>
          </label>
          {cfg.link_button.enabled && (
            <div className="grid sm:grid-cols-2 gap-3 border border-[#1A1A1A] chamfer-sm p-3">
              <div>
                <label className="hud-label block mb-2">Testo del pulsante</label>
                <input value={cfg.link_button.label} onChange={(e) => setL("label")(e.target.value)} maxLength={80} className={inpCls} data-testid="live-notify-link-label" placeholder="GUARDA LA LIVE" />
              </div>
              <div>
                <label className="hud-label block mb-2">Indirizzo del link</label>
                <input value={cfg.link_button.url} onChange={(e) => setL("url")(e.target.value)} className={inpCls} data-testid="live-notify-link-url" placeholder="{url}" />
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button onClick={save} disabled={busy === "save"} data-testid="live-notify-save"
              className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300 disabled:opacity-50">
              {busy === "save" ? "..." : "Salva modello"}
            </button>
            <button onClick={test} disabled={busy === "test"} data-testid="live-notify-test"
              className="chamfer-sm border border-manticore text-manticore hover:bg-manticore hover:text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300 disabled:opacity-50">
              {busy === "test" ? "..." : "Invia annuncio di prova"}
            </button>
          </div>
        </div>
        <div>
          <p className="hud-label mb-2">Anteprima live</p>
          <DiscordPreview
            mention={cfg.mention === "none" ? "none" : "everyone"}
            mentionLabel={cfg.mention === "role" ? "@ruolo-live" : cfg.mention === "everyone" ? "@everyone" : cfg.mention === "here" ? "@here" : ""}
            content={fill(cfg.content)}
            embed={cfg.embed.enabled ? { enabled: true, title: fill(cfg.embed.title), description: fill(cfg.embed.description), color: cfg.embed.color, image_url: cfg.embed.image === "custom" ? cfg.embed.image_url : "" } : {}}
            link={cfg.link_button.enabled ? { enabled: true, label: fill(cfg.link_button.label), url: fill(cfg.link_button.url) } : {}}
            testid="live-notify-preview" />
          <p className="font-mono text-[10px] text-[#4A4D51] mt-3 leading-relaxed">Anteprima indicativa: con "Anteprima della live" la card mostra il fermo immagine reale della diretta.</p>
        </div>
      </div>
    </Box>
  );
}


// Tab "Link Discord": il link di invito ufficiale del server, modificabile dallo Staff
// in autonomia (salvato nelle impostazioni del sito, niente redeploy).
function LinkDiscordTab() {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/config").then(({ data }) => setUrl(data.discord_invite_url || "")).catch(() => setUrl(""));
  useEffect(() => { load(); }, []);
  if (url === null) return null;
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings", { discord_invite_url: url }, { headers: headers() });
      toast.success("Link Discord aggiornato: vale subito su tutto il sito.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "link_non_valido" ? "Il link deve iniziare con https://"
        : err403(e) ? "Permesso Link Discord non assegnato (sezione Staff)." : "Salvataggio non riuscito — controlla la chiave admin.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box title="Link di invito Discord" testid="admin-link-discord">
      <p className="font-mono text-[11px] text-steel leading-relaxed mb-4">
        E' il link del pulsante "Entra nel Discord" nella pagina Discord e dell'invito mostrato ai nuovi utenti non ancora nel server. Cambialo qui: vale da subito su tutto il sito, senza redeploy.
      </p>
      <div className="max-w-2xl flex flex-col gap-4">
        <Field label="Link di invito (https://discord.gg/…)" id="link-discord" value={url} onChange={setUrl} placeholder="https://discord.gg/wigitalia" />
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={save} disabled={busy} data-testid="link-discord-save"
            className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300 disabled:opacity-50">
            {busy ? "..." : "Salva link"}
          </button>
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid="link-discord-open"
              className="chamfer-sm border border-[#1A1A1A] text-steel hover:text-wardogs hover:border-wardogs font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 transition-colors duration-300">
              Apri il link
            </a>
          )}
        </div>
        <p className="font-mono text-[10px] text-[#4A4D51] leading-relaxed">
          Consiglio: usa un invito Discord permanente (mai "scade tra 7 giorni"), cosi' non si rompe piu'.
        </p>
      </div>
    </Box>
  );
}

function BotTab() {
  const [channels, setChannels] = useState([]);
  const [chanErr, setChanErr] = useState(false);
  const [posts, setPosts] = useState(null);
  const [f, setF] = useState(BOT_EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const selCls = "w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300";

  const load = () => {
    api.get("/admin/bot/posts", { headers: headers() }).then(({ data }) => setPosts(data.posts)).catch((e) => toast.error(err403(e) ? "Permesso Bot Discord non assegnato (sezione Staff)." : "Post non caricati."));
    api.get("/admin/bot/channels", { headers: headers() })
      .then(({ data }) => { setChannels(data.channels); setChanErr(false); })
      .catch(() => setChanErr(true));
  };
  useEffect(() => { load(); }, []);

  const payload = (modeOverride) => {
    const mode = modeOverride || f.mode;
    return {
      channel_id: f.channel_id,
      channel_name: channels.find((c) => c.id === f.channel_id)?.name || "",
      content: f.content,
      mention: f.mention,
      embed: { enabled: f.embedOn, title: f.embTitle, description: f.embDesc, url: f.embUrl, color: f.embColor, image_url: f.embImage },
      link_button: { enabled: f.linkOn, label: f.linkLabel, url: f.linkUrl },
      status: mode === "schedule" ? "scheduled" : "draft",
      schedule_at: mode === "schedule" && f.schedule ? new Date(f.schedule).toISOString() : null,
      repeat: mode === "schedule" ? f.repeat : "none",
    };
  };

  const save = async (modeOverride) => {
    if (!f.channel_id) { toast.error("Scegli il canale di destinazione."); return; }
    if (busy) return;
    setBusy("save");
    try {
      if (editing) {
        await api.put(`/admin/bot/posts/${editing}`, payload(modeOverride), { headers: headers() });
        toast.success("Annuncio aggiornato — se era gia' pubblicato, e' stato modificato anche su Discord.");
      } else {
        await api.post("/admin/bot/posts", payload(modeOverride), { headers: headers() });
        toast.success(modeOverride === "schedule" || f.mode === "schedule" ? "Annuncio programmato." : "Bozza salvata.");
      }
      setF(BOT_EMPTY); setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito.");
    } finally {
      setBusy("");
    }
  };

  const sendNow = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/admin/bot/posts/${p.id}/send`, {}, { headers: headers() });
      toast.success("Pubblicato su Discord.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito.");
    } finally {
      setBusy("");
    }
  };

  const del = async (p) => {
    if (!window.confirm("Eliminare questo annuncio? Se e' pubblicato, il messaggio verra' cancellato anche da Discord.")) return;
    setBusy(p.id);
    try {
      await api.delete(`/admin/bot/posts/${p.id}`, { headers: headers() });
      toast.success("Eliminato.");
      load();
    } catch {
      toast.error("Eliminazione non riuscita.");
    } finally {
      setBusy("");
    }
  };

  const editInto = (p) => {
    setEditing(p.id);
    setF({
      channel_id: p.channel_id, channel_name: p.channel_name || "", content: p.content || "", mention: p.mention || "none",
      embedOn: !!p.embed?.enabled, embTitle: p.embed?.title || "", embDesc: p.embed?.description || "",
      embUrl: p.embed?.url || "", embColor: p.embed?.color || "#FF5C00", embImage: p.embed?.image_url || "",
      linkOn: !!p.link_button?.enabled, linkLabel: p.link_button?.label || "", linkUrl: p.link_button?.url || "",
      mode: p.status === "scheduled" ? "schedule" : "now",
      schedule: p.schedule_at ? new Date(p.schedule_at).toISOString().slice(0, 16) : "",
      repeat: p.repeat || "none",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
  const groups = [
    { key: "scheduled", label: "Programmati", color: "text-wardogs" },
    { key: "draft", label: "Bozze", color: "text-steel" },
    { key: "sent", label: "Pubblicati", color: "text-manticore" },
  ];
  const repLabel = { none: "", daily: "· OGNI GIORNO", weekly: "· OGNI SETTIMANA" };

  return (
    <>
      <ManagedMessagesBox />
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
      <Box title={editing ? "Modifica annuncio (si aggiorna anche su Discord)" : "Nuovo messaggio / annuncio"} testid="admin-bot-composer">        <div className="flex flex-col gap-4">
          <div>
            <label className="hud-label block mb-2">Canale di destinazione</label>
            <select value={f.channel_id} onChange={(e) => set("channel_id")(e.target.value)} className={selCls} data-testid="bot-channel-select">
              <option value="">— scegli il canale —</option>
              {channels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
            {chanErr && (
              <div className="mt-2 chamfer-sm border border-red-500/40 bg-red-500/10 px-3 py-2 flex items-center justify-between gap-3" data-testid="bot-channels-error">
                <p className="font-mono text-[10px] text-red-400 leading-relaxed">Canali Discord non caricati: bot irraggiungibile o permesso "Bot Discord" non assegnato (sezione Staff).</p>
                <button onClick={load} className="shrink-0 chamfer-sm border border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white px-3 py-1 font-mono text-[9px] uppercase tracking-[0.12em] transition-colors duration-300" data-testid="bot-channels-retry">Riprova</button>
              </div>
            )}
          </div>
          <div>
            <label className="hud-label block mb-2">Messaggio (usa la toolbar: grassetto, sottolineato, link…)</label>
            <MdEditor value={f.content} onChange={set("content")} rows={5} maxLength={2000} testid="bot-content-input" placeholder="Scrivi qui il testo dell'annuncio…" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="hud-label block mb-2">Menzione</label>
              <select value={f.mention} onChange={(e) => set("mention")(e.target.value)} className={selCls} data-testid="bot-mention-select">
                <option value="none">Nessuna</option>
                <option value="everyone">@everyone (tutti)</option>
                <option value="here">@here (chi e' online)</option>
              </select>
            </div>
            <div>
              <label className="hud-label block mb-2">Invio</label>
              <select value={f.mode} onChange={(e) => set("mode")(e.target.value)} className={selCls} data-testid="bot-mode-select">
                <option value="now">Manuale (bozza o invio immediato)</option>
                <option value="schedule">Programmato (data e ora)</option>
              </select>
            </div>
          </div>
          {f.mode === "schedule" && (
            <div className="grid sm:grid-cols-2 gap-4 chamfer-sm border border-wardogs/30 bg-wardogs/5 p-4">
              <Field label="Data e ora di invio" id="bot-schedule" type="datetime-local" value={f.schedule} onChange={set("schedule")} />
              <div>
                <label className="hud-label block mb-2">Ripeti</label>
                <select value={f.repeat} onChange={(e) => set("repeat")(e.target.value)} className={selCls} data-testid="bot-repeat-select">
                  <option value="none">Mai (una sola volta)</option>
                  <option value="daily">Ogni giorno</option>
                  <option value="weekly">Ogni settimana</option>
                </select>
              </div>
            </div>
          )}
          <div className="chamfer-sm border border-[#1A1A1A] p-4 flex flex-col gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={f.embedOn} onChange={(e) => set("embedOn")(e.target.checked)} className="accent-[#FF5C00]" data-testid="bot-embed-toggle" />
              <span className="hud-label">Card grafica (embed)</span>
            </label>
            {f.embedOn && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Titolo card" id="bot-emb-title" value={f.embTitle} onChange={set("embTitle")} placeholder="Es. TORNEO DEL WEEKEND" />
                  <Field label="Link del titolo (facoltativo)" id="bot-emb-url" value={f.embUrl} onChange={set("embUrl")} placeholder="https://…" />
                </div>
                <div>
                  <label className="hud-label block mb-2">Testo della card</label>
                  <MdEditor value={f.embDesc} onChange={set("embDesc")} rows={4} maxLength={4000} testid="bot-emb-desc-input" placeholder="Descrizione ricca dell'annuncio…" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="hud-label block mb-2">Colore bordo</label>
                    <div className="flex gap-2">
                      {BOT_COLORS.map((c) => (
                        <button key={c.hex} type="button" title={c.label} onClick={() => set("embColor")(c.hex)} data-testid={`bot-color-${c.hex.slice(1)}`}
                          className={`w-9 h-9 chamfer-sm border-2 transition-all duration-200 ${f.embColor === c.hex ? "border-white scale-110" : "border-transparent"}`}
                          style={{ background: c.hex }} />
                      ))}
                    </div>
                  </div>
                  <Field label="Immagine card — URL (facoltativo)" id="bot-emb-image" value={f.embImage} onChange={set("embImage")} placeholder="https://…/immagine.png" />
                </div>
              </>
            )}
          </div>
          <div className="chamfer-sm border border-[#1A1A1A] p-4 flex flex-col gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={f.linkOn} onChange={(e) => set("linkOn")(e.target.checked)} className="accent-[#FF5C00]" data-testid="bot-link-toggle" />
              <span className="hud-label">Pulsante link sotto il messaggio</span>
            </label>
            {f.linkOn && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Testo del pulsante" id="bot-link-label" value={f.linkLabel} onChange={set("linkLabel")} placeholder="Es. ISCRIVITI AL TORNEO" />
                <Field label="Indirizzo del link" id="bot-link-url" value={f.linkUrl} onChange={set("linkUrl")} placeholder="https://…" />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {editing ? (
              <>
                <SaveButton label="Salva modifiche" busy={busy === "save"} testid="bot-save-btn" onClick={() => save()} />
                <button onClick={() => { setEditing(null); setF(BOT_EMPTY); }} className="chamfer-sm border border-[#2A2A2A] text-steel hover:text-white font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300" data-testid="bot-cancel-edit">Annulla</button>
              </>
            ) : (
              <>
                <SaveButton label="Salva bozza" busy={busy === "save"} testid="bot-save-btn" onClick={() => save("draft")} />
                <button onClick={() => save("schedule")} disabled={!!busy} className="chamfer-sm border border-wardogs text-wardogs hover:bg-wardogs hover:text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-5 py-3 transition-colors duration-300 disabled:opacity-50" data-testid="bot-schedule-btn">
                  {f.mode === "schedule" ? "Programma invio" : "Salva programmato"}
                </button>
                <button onClick={async () => { await save("draft"); }} disabled={!!busy} className="hidden" aria-hidden="true" tabIndex={-1} />
              </>
            )}
          </div>
        </div>
      </Box>

      <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6" data-testid="bot-preview">
        <p className="hud-label mb-4">Anteprima live</p>
        <DiscordPreview
          mention={f.mention}
          content={f.content}
          channels={channels}
          embed={{ enabled: f.embedOn, title: f.embTitle, description: f.embDesc, color: f.embColor, image_url: f.embImage }}
          link={{ enabled: f.linkOn, label: f.linkLabel, url: f.linkUrl }}
          testid="bot-preview-discord" />
        <p className="font-mono text-[10px] text-[#4A4D51] mt-4 leading-relaxed">Anteprima indicativa: la grafica finale su Discord puo' variare leggermente.</p>
      </div>

      <div className="lg:col-span-2">
        <Box title="Annunci gestiti" testid="admin-bot-list">
          {!posts ? (
            <p className="font-mono text-xs text-steel">Caricamento…</p>
          ) : posts.length === 0 ? (
            <p className="font-mono text-xs text-steel">Nessun annuncio ancora. Crealo qui sopra.</p>
          ) : (
            <div className="flex flex-col gap-6">
              {groups.map((g) => {
                const list = posts.filter((p) => p.status === g.key);
                if (list.length === 0) return null;
                return (
                  <div key={g.key}>
                    <p className={`hud-label mb-3 ${g.color}`}>{g.label} ({list.length})</p>
                    <div className="flex flex-col gap-2">
                      {list.map((p) => (
                        <div key={p.id} className="chamfer-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`bot-post-${p.id}`}>
                          <span className="chamfer-sm px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] font-bold text-wardogs border border-wardogs/40 shrink-0">#{p.channel_name || p.channel_id}</span>
                          <span className="font-mono text-xs text-white truncate flex-1 min-w-[200px]">{p.embed?.enabled && p.embed?.title ? `[CARD] ${p.embed.title}` : (p.content || "(card)")}</span>
                          {p.status === "scheduled" && (
                            <span className="font-mono text-[10px] text-wardogs shrink-0">{fmtWhen(p.schedule_at)} {repLabel[p.repeat || "none"]}</span>
                          )}
                          {p.status === "sent" && p.sent_at && (
                            <span className="font-mono text-[10px] text-[#4A4D51] shrink-0">{fmtWhen(p.sent_at)}</span>
                          )}
                          <div className="flex gap-2 shrink-0">
                            <button onClick={() => editInto(p)} className="chamfer-sm border border-[#2A2A2A] text-steel hover:text-white px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] transition-colors duration-300" data-testid={`bot-edit-${p.id}`}>Modifica</button>
                            {p.status !== "sent" && (
                              <button onClick={() => sendNow(p)} disabled={busy === p.id} className="chamfer-sm border border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759] hover:text-[#050505] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] transition-colors duration-300 disabled:opacity-50" data-testid={`bot-send-${p.id}`}>Invia ora</button>
                            )}
                            <button onClick={() => del(p)} disabled={busy === p.id} className="chamfer-sm border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] transition-colors duration-300 disabled:opacity-50" data-testid={`bot-delete-${p.id}`}>Elimina</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Box>
      </div>
      </div>
    </>
  );
}

function EventiTab() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title: "", date: "", description: "", link: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/events").then(({ data }) => setEvents(data.events)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/events", { ...form, date: new Date(form.date).toISOString() }, { headers: headers() });
      toast.success(data.discord_event ? "Evento creato sul sito e pubblicato come Evento Programmato su Discord." : "Evento creato sul sito. Attenzione: non sincronizzato su Discord (controlla il bot).");
      setForm({ title: "", date: "", description: "", link: "" });
      load();
    } catch (err) {
      toast.error(err403(err) ? "Chiave admin non valida." : "Errore nella creazione.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`, { headers: headers() });
      toast.success("Evento rimosso.");
      load();
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };

  return (
    <>
      <Box title="Nuovo evento community" testid="admin-event-form">
        <p className="font-mono text-[10px] text-steel mb-5 leading-relaxed">L'evento creato qui appare nella homepage e viene pubblicato automaticamente come Evento Programmato sul server Discord, visibile a tutti i membri. Eliminandolo da qui viene rimosso anche da Discord.</p>
        <form onSubmit={add}>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Field label="Titolo" id="ev-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required placeholder="es. Serata WARDOGS insieme" />
            <Field label="Data e ora" id="ev-date" type="datetime-local" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-5">
            <Field label="Descrizione (opzionale)" id="ev-desc" value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="Dettagli evento…" />
            <Field label="Link (opzionale)" id="ev-link" value={form.link} onChange={(v) => setForm({ ...form, link: v })} placeholder="https://discord.gg/…" />
          </div>
          <SaveButton label="Crea evento" busy={busy} testid="admin-event-create-button" />
        </form>
      </Box>
      <p className="hud-label mb-4">Eventi ({events.length})</p>
      <div className="flex flex-col gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
        {events.map((ev) => (
          <RowItem key={ev.id} testid={`admin-event-${ev.id}`} title={ev.title} sub={new Date(ev.date).toLocaleString("it-IT")} onRemove={() => remove(ev.id)} />
        ))}
      </div>
    </>
  );
}

const WARDOGS_IMG = "/logo-wardogs.png";

function GameEventsTab() {
  const empty = { name: "", date: "", duration_hours: 3, platform: "twitch", channel: "", youtube_url: "", description: "", color: "#FF5C00", recurring: false, estimated: false, enabled: true };
  const [items, setItems] = useState([]);
  const [chatMode, setChatMode] = useState("twitch");
  const [autoDates, setAutoDates] = useState(true);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/admin/game-events", { headers: headers() }).then(({ data }) => { setItems(data.events); setChatMode(data.chat_mode || "twitch"); }).catch(() => {});
    api.get("/admin/settings", { headers: headers() }).then(({ data }) => setAutoDates(data.events_auto_dates !== false)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleAutoDates = async () => {
    const next = !autoDates;
    setAutoDates(next);
    try {
      await api.post("/settings", { events_auto_dates: next }, { headers: headers() });
      toast.success(next ? "Aggiornamento automatico delle date attivo (controllo ogni 7 giorni)." : "Aggiornamento automatico delle date disattivato.");
    } catch {
      setAutoDates(!next);
      toast.error("Chiave admin non valida.");
    }
  };

  const inputCls = "w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300";
  const toLocalInput = (iso) => { try { return new Date(iso).toLocaleString("sv-SE").replace(" ", "T").slice(0, 16); } catch { return ""; } };

  const setMode = async (mode) => {
    try {
      const { data } = await api.post("/admin/game-events/settings", { chat_mode: mode }, { headers: headers() });
      setChatMode(data.chat_mode);
      toast.success(mode === "twitch" ? "Chat Twitch incorporata attiva nelle sale eventi." : "Chat interna del sito attiva nelle sale eventi.");
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (busy || !form.date) return;
    setBusy(true);
    const payload = { ...form, date: new Date(form.date).toISOString(), duration_hours: Number(form.duration_hours) || 3 };
    try {
      if (editId) {
        await api.put(`/admin/game-events/${editId}`, payload, { headers: headers() });
        toast.success("Evento aggiornato.");
      } else {
        await api.post("/admin/game-events", payload, { headers: headers() });
        toast.success("Evento aggiunto alla pagina Eventi.");
      }
      setForm(empty);
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err403(err) ? "Chiave admin non valida." : "Errore: controlla nome, data e canale/URL della diretta.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/admin/game-events/${id}`, { headers: headers() });
      toast.success("Evento eliminato.");
      if (editId === id) { setEditId(null); setForm(empty); }
      load();
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };
  const startEdit = (it) => {
    setEditId(it.id);
    setForm({
      name: it.name, date: toLocalInput(it.date), duration_hours: it.duration_hours || 3,
      platform: it.platform || "twitch", channel: it.channel || "", youtube_url: it.youtube_url || "",
      description: it.description || "", color: it.color || "#FF5C00",
      recurring: !!it.recurring, estimated: !!it.estimated, enabled: it.enabled !== false,
    });
  };

  const checkCls = (key, label, testid) => (
    <label key={key} className={`chamfer-sm border px-4 py-3 flex items-center gap-2.5 cursor-pointer font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-300 ${form[key] ? "border-wardogs/50 text-white" : "border-[#1A1A1A] text-steel"}`}>
      <input type="checkbox" checked={!!form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })} className="accent-[#FF5C00]" data-testid={testid} />
      {label}
    </label>
  );

  return (
    <>
      <Box title="Chat delle sale eventi" testid="admin-ge-chatmode">
        <p className="font-mono text-[10px] text-steel mb-4 leading-relaxed">Un click per scegliere quale chat mostrare nelle sale "guarda insieme". Vale per tutti gli eventi con diretta Twitch (gli eventi YouTube usano sempre la chat interna).</p>
        <div className="flex gap-3">
          {[["twitch", "Chat Twitch"], ["internal", "Chat interna del sito"]].map(([mode, label]) => (
            <button key={mode} onClick={() => setMode(mode)} className={`chamfer-sm px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold transition-colors duration-300 ${chatMode === mode ? "bg-wardogs text-[#050505]" : "border border-[#2A2A2A] text-steel hover:text-white hover:border-wardogs"}`} data-testid={`chat-mode-${mode}`}>
              {label}
            </button>
          ))}
        </div>
      </Box>

      <ChatModerationBox />

      <Box title="Date eventi automatiche" testid="admin-ge-autodates">
        <p className="font-mono text-[10px] text-steel mb-4 leading-relaxed">Ogni 7 giorni il sistema controlla le date ufficiali più recenti dei grandi eventi annuali: se una data annunciata cambia, la aggiorna da solo (resta marcata "data stimata") e ti avvisa su Discord. Per conferme al 100% puoi sempre correggere a mano dalla lista qui sotto.</p>
        <button onClick={toggleAutoDates} className={`chamfer-sm px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold transition-colors duration-300 ${autoDates ? "bg-wardogs text-[#050505]" : "border border-[#2A2A2A] text-steel hover:text-white hover:border-wardogs"}`} data-testid="auto-dates-toggle">
          {autoDates ? "Attivo — controllo ogni 7 giorni" : "Disattivato"}
        </button>
      </Box>

      <Box title={editId ? "Modifica evento" : "Nuovo evento gaming"} testid="admin-ge-form">
        <p className="font-mono text-[10px] text-steel mb-5 leading-relaxed">Gli eventi appaiono nella pagina Eventi con countdown live; gli annuali si rinnovano da soli (+1 anno, marcati "data stimata"). Il bot Discord manda i promemoria automatici: giorno prima, un'ora prima e all'inizio della diretta. Il canale Twitch serve per player e chat incorporati — nessun link esterno, si guarda tutto dal sito.</p>
        <form onSubmit={save}>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Field label="Nome evento" id="ge-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="es. FPS Gameshow" required />
            <Field label="Data e ora (ora italiana)" id="ge-date" type="datetime-local" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Field label="Durata (ore)" id="ge-duration" type="number" value={form.duration_hours} onChange={(v) => setForm({ ...form, duration_hours: v })} hint="Serve per la finestra LIVE e i promemoria." />
            <div>
              <label className="hud-label block mb-2" htmlFor="ge-platform">Piattaforma diretta</label>
              <select id="ge-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className={inputCls} data-testid="ge-platform-select">
                <option value="twitch">Twitch</option>
                <option value="youtube">YouTube</option>
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {form.platform === "twitch" ? (
              <Field label="Canale Twitch della diretta" id="ge-channel" value={form.channel} onChange={(v) => setForm({ ...form, channel: v })} placeholder="es. thegameawards" />
            ) : (
              <Field label="URL o ID diretta YouTube" id="ge-yt" value={form.youtube_url} onChange={(v) => setForm({ ...form, youtube_url: v })} placeholder="https://youtube.com/watch?v=…" />
            )}
            <div>
              <label className="hud-label block mb-2" htmlFor="ge-color">Colore accento</label>
              <input id="ge-color" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-12 h-11 bg-[#050505] border border-[#1A1A1A] cursor-pointer" data-testid="ge-color-input" />
            </div>
          </div>
          <div className="mb-4">
            <label className="hud-label block mb-2" htmlFor="ge-desc">Descrizione (facoltativa)</label>
            <textarea id="ge-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} maxLength={500} className={inputCls} placeholder="Di cosa parla lo show…" data-testid="ge-desc-input" />
          </div>
          <div className="flex flex-wrap gap-3 mb-5">
            {[checkCls("recurring", "Annuale (si rinnova)", "ge-recurring-check"), checkCls("estimated", "Data stimata", "ge-estimated-check"), checkCls("enabled", "Visibile sul sito", "ge-enabled-check")]}
          </div>
          <div className="flex items-center gap-3">
            <SaveButton label={editId ? "Salva modifiche" : "Aggiungi evento"} busy={busy} testid="admin-ge-save-button" />
            {editId && (
              <button type="button" onClick={() => { setEditId(null); setForm(empty); }} className="chamfer-sm border border-[#2A2A2A] hover:border-valkyra text-steel hover:text-white font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-3.5 transition-colors duration-300" data-testid="admin-ge-cancel-edit">
                Annulla
              </button>
            )}
          </div>
        </form>
      </Box>

      <p className="hud-label mb-4">Eventi in calendario ({items.length})</p>
      <div className="flex flex-col gap-px bg-[#1A1A1A] border border-[#1A1A1A]" data-testid="admin-ge-list">
        {items.length === 0 && <p className="bg-[#050505] p-5 font-mono text-[11px] text-[#4A4D51]" data-testid="admin-ge-empty">Nessun evento.</p>}
        {items.map((it) => {
          const past = new Date() > new Date(new Date(it.date).getTime() + (it.duration_hours || 3) * 3600000);
          return (
            <div key={it.id} className="bg-[#050505] p-5 flex items-center gap-4" data-testid={`admin-ge-${it.id}`}>
              <span className="w-2 h-8 chamfer-sm shrink-0" style={{ background: it.color || "#FF5C00" }} />
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl uppercase truncate">
                  {it.name}
                  {it.enabled === false && <span className="font-mono text-[9px] text-valkyra ml-2">NASCOSTO</span>}
                  {it.estimated && <span className="font-mono text-[9px] text-[#4A4D51] ml-2">STIMATA</span>}
                  {past && <span className="font-mono text-[9px] text-steel ml-2">PASSATO</span>}
                </p>
                <p className="font-mono text-[10px] text-steel truncate">
                  {new Date(it.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}
                  {" · "}{it.platform === "twitch" ? `twitch.tv/${it.channel}` : "YouTube"}
                  {it.recurring ? " · annuale" : ""}
                </p>
              </div>
              <button onClick={() => startEdit(it)} data-testid={`admin-ge-edit-${it.id}`} className="text-steel hover:text-wardogs transition-colors duration-300" aria-label="Modifica">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => remove(it.id)} data-testid={`admin-ge-del-${it.id}`} className="text-steel hover:text-valkyra transition-colors duration-300" aria-label="Elimina">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ChatModerationBox() {
  const [mod, setMod] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/chat-moderation", { headers: headers() }).then(({ data }) => setMod(data.moderation)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!mod) return null;
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/admin/chat-moderation", mod, { headers: headers() });
      toast.success("Moderazione chat salvata — attiva subito.");
    } catch {
      toast.error("Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };

  const TOGGLES = [
    ["block_zalgo", "Blocca testo zalgo (caratteri glitciati)", "Il testo c0rr0tt0 con simboli impilati viene rifiutato."],
    ["block_slurs", "Parole vietate globali", "Insulti razziali, omofobi e transfobici (anche con trucchi tipo l33t). Le parolacce e le bestemmie restano libere."],
    ["block_links", "Blocca link", "Niente URL in chat: anti phishing e scam."],
    ["block_caps", "Blocca tutto maiuscolo", "Messaggi urlati in CAPS LOCK vengono rifiutati."],
    ["block_repeat", "Blocca flood di caratteri", "Sequenze tipo aaaaaaaaaa vengono rifiutate."],
  ];

  return (
    <Box title="Moderazione chat interna" testid="admin-chat-mod">
      <p className="font-mono text-[10px] text-steel mb-5 leading-relaxed">Vale solo per la chat interna del sito (quella Twitch segue le regole del canale). Lo staff è sempre esente dai filtri. Le parolacce e le bestemmie non vengono filtrate: la chat resta italiana.</p>
      <div className="flex flex-col gap-3 mb-5">
        {TOGGLES.map(([key, label, hint]) => (
          <label key={key} className={`chamfer-sm border px-4 py-3.5 flex items-start gap-3 cursor-pointer transition-colors duration-300 ${mod[key] ? "border-wardogs/50 bg-wardogs/5" : "border-[#1A1A1A]"}`} data-testid={`mod-${key}`}>
            <input type="checkbox" checked={!!mod[key]} onChange={(e) => setMod({ ...mod, [key]: e.target.checked })} className="accent-[#FF5C00] mt-0.5" data-testid={`mod-${key}-check`} />
            <span>
              <span className={`font-mono text-[11px] uppercase tracking-[0.12em] block ${mod[key] ? "text-white" : "text-steel"}`}>{label}</span>
              <span className="font-mono text-[10px] text-[#4A4D51] leading-relaxed block mt-1">{hint}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="hud-label block mb-2" htmlFor="mod-slow">Slow mode (secondi tra i messaggi, 0 = disattivata)</label>
          <input id="mod-slow" type="number" min="0" max="60" value={mod.slow_mode} onChange={(e) => setMod({ ...mod, slow_mode: Number(e.target.value) })} className="w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300" data-testid="mod-slow-input" />
        </div>
        <div>
          <label className="hud-label block mb-2" htmlFor="mod-words">Parole vietate personalizzate (una per riga o separate da virgola)</label>
          <textarea id="mod-words" rows={3} value={mod.custom_words} onChange={(e) => setMod({ ...mod, custom_words: e.target.value })} className="w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300" placeholder="parola1&#10;parola2" data-testid="mod-words-input" />
        </div>
      </div>
      <SaveButton label="Salva moderazione" busy={busy} testid="admin-chat-mod-save" onClick={save} />
    </Box>
  );
}

function ProgrammazioneTab() {
  const KINDS = [
    { id: "live", label: "Live", color: "#34C759" },
    { id: "off", label: "Niente live", color: "#FF3B30" },
    { id: "special", label: "Speciale", color: "#FF5C00" },
  ];
  const KIND_DOT = { live: "#34C759", off: "#FF3B30", special: "#FF5C00" };
  const PALETTE = ["#34C759", "#FF5C00", "#FF3B30", "#00A3FF", "#BF5AF2", "#FFD60A", "#FFFFFF"];
  const DAY_NAMES = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
  const DAY_SHORT = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
  const HOLIDAYS_FIXED = {
    "01-01": "Capodanno", "06-01": "Epifania", "25-04": "Festa della Liberazione", "01-05": "Festa dei Lavoratori",
    "02-06": "Festa della Repubblica", "15-08": "Ferragosto", "01-11": "Ognissanti", "08-12": "Immacolata",
    "25-12": "Natale", "26-12": "Santo Stefano",
  };
  const empty = { date: "", time: "", title: "", kind: "live", color: "", image: "", double: false, time2: "16:00", title2: "", image2: "" };
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [selected, setSelected] = useState("");
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekDays, setWeekDays] = useState([]);
  const [weekBusy, setWeekBusy] = useState(false);
  const load = () => api.get("/schedule").then(({ data }) => setItems(data.schedule)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/game-events").then(({ data }) => setEvents(data.events || [])).catch(() => {});
  }, []);

  const inputCls = "w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-3 py-2.5 font-mono text-xs text-white transition-colors duration-300";
  const pad2 = (n) => String(n).padStart(2, "0");
  const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  // Festività italiane: fisse + Pasqua/Pasquetta (algoritmo di Meeus)
  const easter = (y) => {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
    return new Date(y, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1);
  };
  const holidayOf = (d) => {
    const fixed = HOLIDAYS_FIXED[`${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`];
    if (fixed) return fixed;
    const pasqua = easter(d.getFullYear());
    if (d.toDateString() === pasqua.toDateString()) return "Pasqua";
    const pasquetta = new Date(pasqua);
    pasquetta.setDate(pasqua.getDate() + 1);
    if (d.toDateString() === pasquetta.toDateString()) return "Pasquetta";
    return null;
  };

  const Swatches = ({ value, kindId, onPick, prefix }) => {
    const kindColor = KIND_DOT[kindId] || "#34C759";
    return (
      <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${prefix}-swatches`}>
        <button type="button" onClick={() => onPick("")} title="Colore automatico del tipo"
          className={`w-6 h-6 chamfer-sm border transition-transform duration-200 hover:scale-110 ${value === "" ? "border-white ring-1 ring-white" : "border-[#2A2A2A]"}`}
          style={{ background: `linear-gradient(135deg, ${kindColor} 50%, #1A1A1A 50%)` }} data-testid={`${prefix}-swatch-auto`} />
        {PALETTE.map((c) => (
          <button key={c} type="button" onClick={() => onPick(c)} title={c}
            className={`w-6 h-6 chamfer-sm border transition-transform duration-200 hover:scale-110 ${value === c ? "border-white ring-1 ring-white" : "border-[#2A2A2A]"}`}
            style={{ background: c }} data-testid={`${prefix}-swatch-${c.replace("#", "")}`} />
        ))}
        <input type="color" value={value || kindColor} onChange={(e) => onPick(e.target.value)}
          className="w-6 h-6 bg-[#050505] border border-[#1A1A1A] cursor-pointer" title="Colore personalizzato" data-testid={`${prefix}-swatch-custom`} />
      </div>
    );
  };

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { date: form.date, time: form.time, title: form.title, kind: form.kind, color: form.color || "", image: form.image };
      if (editId) {
        await api.put(`/admin/schedule/${editId}`, payload, { headers: headers() });
        toast.success("Voce del calendario aggiornata.");
      } else {
        const payloads = [payload];
        if (form.double && form.time2 && form.time2 !== form.time) payloads.unshift({ ...payload, time: form.time2, title: form.title2, image: form.image2 });
        for (const p of payloads) {
          await api.post("/admin/schedule", p, { headers: headers() });
        }
        toast.success(payloads.length > 1 ? `Doppia live aggiunta: ${form.time2} e ${form.time || "senza orario"}.` : "Voce aggiunta al calendario della pagina Streamer.");
      }
      setForm({ ...empty, date: selected });
      setEditId(null);
      load();
    } catch (err) {
      toast.error(err403(err) ? "Chiave admin non valida." : "Errore: controlla data, ora e colore (#RRGGBB).");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/admin/schedule/${id}`, { headers: headers() });
      toast.success("Voce rimossa dal calendario.");
      if (editId === id) { setEditId(null); setForm({ ...empty, date: selected }); }
      load();
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };
  const startEdit = (it) => {
    setEditId(it.id);
    setForm({ date: it.date, time: it.time || "", title: it.title || "", kind: it.kind, color: it.color || "", image: it.image || "", double: false, time2: "16:00" });
  };
  const selectDay = (iso) => {
    setSelected(iso);
    if (!editId) setForm((f) => ({ ...f, date: iso }));
  };

  // ----- Griglia del mese -----
  const year = month.getFullYear();
  const mo = month.getMonth();
  const startShift = (new Date(year, mo, 1).getDay() + 6) % 7; // lunedì = 0
  const gridStart = new Date(year, mo, 1 - startShift);
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(gridStart);
      day.setDate(gridStart.getDate() + w * 7 + d);
      row.push(day);
    }
    if (row.every((d) => d.getMonth() !== mo)) break;
    weeks.push(row);
  }
  const todayIso = isoOf(new Date());
  const itemsByDate = {};
  items.forEach((it) => { (itemsByDate[it.date] = itemsByDate[it.date] || []).push(it); });
  const eventsByDate = {};
  events.forEach((ev) => {
    const d = new Date(ev.date);
    if (isNaN(d)) return;
    (eventsByDate[isoOf(d)] = eventsByDate[isoOf(d)] || []).push(ev);
  });
  const monthLabel = month.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const selHoliday = selected ? holidayOf(new Date(`${selected}T12:00:00`)) : null;

  // ----- Settimana automatica -----
  // La bozza resta salvata in questo browser (localStorage): mai piu' reset.
  // All'apertura la settimana si precompila dalle voci gia' presenti nel DB.
  const n0 = new Date();
  const weekMonday = new Date(n0.getFullYear(), n0.getMonth(), n0.getDate());
  weekMonday.setDate(weekMonday.getDate() - ((weekMonday.getDay() + 6) % 7) + weekOffset * 7);
  const weekKey = isoOf(weekMonday);
  const weekSunday = new Date(weekMonday);
  weekSunday.setDate(weekMonday.getDate() + 6);
  const DRAFT_KEY = `wd_week_draft_${weekKey}`;
  const fmtDay = (d) => `${d.getDate()}/${pad2(d.getMonth() + 1)}`;
  const emptyDay = (name, date) => ({ name, date, on: false, time: "21:30", title: "", image: "", double: false, time2: "16:00", title2: "", image2: "", kind: "live", color: "" });

  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed?.length === 7 && parsed[0]?.date === weekKey) { setWeekDays(parsed); return; }
      } catch { /* bozza corrotta: si ricostruisce */ }
    }
    const byDate = {};
    items.forEach((it) => { (byDate[it.date] = byDate[it.date] || []).push(it); });
    setWeekDays(DAY_NAMES.map((name, i) => {
      const d = new Date(weekMonday);
      d.setDate(weekMonday.getDate() + i);
      const day = emptyDay(name, isoOf(d));
      const its = (byDate[day.date] || []).slice().sort((a, b) => (a.time || "").localeCompare(b.time || ""));
      if (!its.length) return day;
      // la voce piu' tarda e' la live serale (sessione 1), la prima del giorno la pomeridiana (sessione 2)
      const evening = its[its.length - 1];
      const afternoon = its.length > 1 ? its[0] : null;
      const merged = { ...day, on: true, time: evening.time || "", title: evening.title || "", image: evening.image || "", kind: evening.kind, color: evening.color || "" };
      if (afternoon) Object.assign(merged, { double: true, time2: afternoon.time || "", title2: afternoon.title || "", image2: afternoon.image || "" });
      return merged;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekKey, items]);

  useEffect(() => {
    if (weekDays.length === 7 && weekDays[0]?.date === weekKey) localStorage.setItem(DRAFT_KEY, JSON.stringify(weekDays));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDays]);

  const setDay = (i, patch) => setWeekDays((wd) => wd.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const saveWeek = async () => {
    if (weekBusy) return;
    setWeekBusy(true);
    try {
      const days = weekDays.map((d) => {
        const entries = [];
        if (d.on) {
          entries.push({ date: d.date, time: d.time, title: d.title, kind: d.kind, color: d.color, image: d.image });
          if (d.double && d.time2 && d.time2 !== d.time) entries.push({ date: d.date, time: d.time2, title: d.title2, kind: d.kind, color: d.color, image: d.image2 });
        }
        return { date: d.date, entries };
      });
      const { data } = await api.post("/admin/schedule/week", { days }, { headers: headers() });
      toast.success(`Settimana salvata: ${data.added} voci attive. I giorni spenti sono stati svuotati.`);
      localStorage.removeItem(DRAFT_KEY);
      load();
    } catch (err) {
      toast.error(err403(err) ? "Chiave admin non valida." : "Errore nel salvataggio della settimana.");
    } finally {
      setWeekBusy(false);
    }
  };

  return (
    <>
      <Box title="Calendario" testid="admin-schedule-calendar">
        <p className="font-mono text-[10px] text-steel mb-5 leading-relaxed">
          Il mese in stile calendario vero: vedi giorni feriali, festività italiane (in magenta) ed eventi gaming reali (riga colorata).
          Click su un giorno per gestirlo: le voci esistenti si aprono sotto il calendario e il modulo qui in basso parte già con quella data.
        </p>
        <div className="flex items-center justify-between mb-4">
          <button type="button" onClick={() => setMonth(new Date(year, mo - 1, 1))} className="chamfer-sm border border-[#2A2A2A] hover:border-wardogs text-steel hover:text-white px-4 py-2 font-mono text-sm transition-colors duration-300" data-testid="cal-prev-month">‹</button>
          <p className="font-display text-2xl uppercase tracking-wide" data-testid="cal-month-label">{monthLabel}</p>
          <button type="button" onClick={() => setMonth(new Date(year, mo + 1, 1))} className="chamfer-sm border border-[#2A2A2A] hover:border-wardogs text-steel hover:text-white px-4 py-2 font-mono text-sm transition-colors duration-300" data-testid="cal-next-month">›</button>
        </div>
        <div className="grid grid-cols-7 gap-px bg-[#1A1A1A] border border-[#1A1A1A]" data-testid="cal-grid">
          {DAY_SHORT.map((d) => (
            <p key={d} className="bg-[#0C0C0C] py-1.5 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-[#4A4D51]">{d}</p>
          ))}
          {weeks.flat().map((day) => {
            const iso = isoOf(day);
            const inMonth = day.getMonth() === mo;
            const hol = holidayOf(day);
            const dayItems = itemsByDate[iso] || [];
            const dayEvents = eventsByDate[iso] || [];
            const weekend = day.getDay() === 0 || day.getDay() === 6;
            return (
              <div key={iso} onClick={() => selectDay(iso)} role="button" tabIndex={0}
                className={`min-h-[112px] p-1.5 cursor-pointer transition-colors duration-200 hover:bg-[#0E0E0E] ${inMonth ? (iso === todayIso ? "bg-[#0C0C0C]" : "bg-[#050505]") : "bg-[#030303] opacity-40"} ${iso === selected ? "outline outline-1 outline-wardogs" : ""}`}
                data-testid={`cal-day-${iso}`}>
                <p className={`font-mono text-[10px] font-bold ${iso === todayIso ? "text-wardogs" : weekend || hol ? "text-steel" : "text-white"}`}>
                  {day.getDate()}{iso === todayIso ? " • oggi" : ""}
                </p>
                {hol && <p className="font-mono text-[8px] uppercase tracking-wide text-valkyra leading-tight mt-0.5" data-testid={`cal-holiday-${iso}`}>{hol}</p>}
                {dayEvents.map((ev) => (
                  <p key={ev.id} className="font-mono text-[8px] uppercase truncate mt-1 pl-1 border-l-2" style={{ borderColor: ev.color || "#FF5C00", color: ev.color || "#FF5C00" }} title={ev.name} data-testid={`cal-event-${ev.slug}`}>
                    {ev.name}
                  </p>
                ))}
                {dayItems.map((it) => (
                  <p key={it.id} onClick={(e) => { e.stopPropagation(); startEdit(it); }}
                    className="font-mono text-[9px] truncate mt-1 flex items-center gap-1 cursor-pointer text-steel hover:text-white" title="Click per modificare" data-testid={`cal-entry-${it.id}`}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 inline-block" style={{ background: it.color || KIND_DOT[it.kind] || "#34C759" }} />
                    {it.time ? `${it.time} ` : ""}{it.title || KINDS.find((k) => k.id === it.kind)?.label}
                  </p>
                ))}
              </div>
            );
          })}
        </div>
        {selected && (
          <div className="mt-4 border border-wardogs/30 bg-[#0C0C0C] p-4" data-testid="cal-day-panel">
            <p className="hud-label mb-3">
              {DAY_NAMES[(new Date(`${selected}T12:00:00`).getDay() + 6) % 7]} {selected.split("-").reverse().join("/")}{selHoliday ? ` — ${selHoliday}` : ""}
            </p>
            {(itemsByDate[selected] || []).length === 0 && (
              <p className="font-mono text-[10px] text-[#4A4D51]">Nessuna voce in questo giorno: compila il modulo qui sotto, la data è già impostata.</p>
            )}
            {(itemsByDate[selected] || []).map((it) => (
              <div key={it.id} className="flex items-center gap-3 py-1.5" data-testid={`cal-panel-entry-${it.id}`}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: it.color || KIND_DOT[it.kind] || "#34C759" }} />
                <span className="font-mono text-[11px] text-white flex-1 truncate">
                  {it.time ? `${it.time} — ` : ""}{it.title || KINDS.find((k) => k.id === it.kind)?.label}
                </span>
                <button type="button" onClick={() => startEdit(it)} data-testid={`cal-panel-edit-${it.id}`} className="text-steel hover:text-wardogs transition-colors duration-300" aria-label="Modifica">
                  <Pencil className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => remove(it.id)} data-testid={`cal-panel-del-${it.id}`} className="text-steel hover:text-valkyra transition-colors duration-300" aria-label="Rimuovi">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Box>

      <Box title={editId ? "Modifica voce del calendario" : "Nuova voce del calendario"} testid="admin-schedule-form">
        <p className="font-mono text-[10px] text-steel mb-5 leading-relaxed">La programmazione appare nella colonna sinistra della pagina Streamer, accanto al player. Usa "Niente live" per segnalare i giorni di pausa e "Speciale" per eventi particolari. Le date passate si cancellano da sole. Senza colore scelto vale quello del tipo.</p>
        <form onSubmit={save}>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Field label="Data" id="sch-date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
            <Field label="Ora (facoltativa)" id="sch-time" type="time" value={form.time} onChange={(v) => setForm({ ...form, time: v })} />
          </div>
          {!editId && (
            <div className="mb-4 chamfer-sm border border-[#1A1A1A] bg-[#080808] p-3.5" data-testid="sch-double-block">
              <button type="button" onClick={() => setForm({ ...form, double: !form.double })} className={`chamfer-sm px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] font-bold transition-colors duration-300 ${form.double ? "bg-wardogs text-[#050505]" : "border border-[#2A2A2A] text-steel hover:text-white"}`} data-testid="sch-double-toggle">
                Doppia live {form.double ? "ON" : "OFF"}
              </button>
              {form.double && (
                <div className="mt-3 flex flex-col gap-3">
                  <div className="flex items-end gap-3 flex-wrap">
                    <div className="w-36">
                      <Field label="Ora pomeriggio" id="sch-time2" type="time" value={form.time2} onChange={(v) => setForm({ ...form, time2: v })} />
                    </div>
                    <p className="font-mono text-[10px] text-steel pb-2.5">Vengono create due voci nello stesso giorno: la serale usa gioco e immagine del modulo, la pomeridiana ha i suoi qui sotto.</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Gioco / contenuto pomeriggio" id="sch-title2" value={form.title2} onChange={(v) => setForm({ ...form, title2: v })} placeholder="es. Mortal Shell 2" />
                    <div>
                      <label className="hud-label block mb-2" htmlFor="sch-image2">Immagine pomeriggio (URL, facoltativa)</label>
                      <div className="flex items-center gap-2">
                        <input id="sch-image2" type="text" value={form.image2} onChange={(e) => setForm({ ...form, image2: e.target.value })} placeholder="https://…" className={inputCls} data-testid="sch-image2-input" />
                        <button type="button" onClick={() => setForm({ ...form, image2: WARDOGS_IMG })} className="chamfer-sm border border-wardogs/50 text-wardogs hover:bg-wardogs hover:text-[#050505] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] whitespace-nowrap transition-colors duration-300" data-testid="sch-image2-wardogs">
                          WARDOGS
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <Field label="Contenuto / gioco (facoltativo)" id="sch-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="es. WARDOGS ranked con la community" />
            <div>
              <label className="hud-label block mb-2" htmlFor="sch-kind">Tipo</label>
              <select id="sch-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, color: "" })} className="w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300" data-testid="sch-kind-select">
                {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="hud-label block mb-2">Colore (cambia col tipo — tocchi un quadratino e la voce prende quel colore)</label>
              <Swatches value={form.color} kindId={form.kind} onPick={(c) => setForm({ ...form, color: c })} prefix="sch" />
            </div>
            <div>
              <label className="hud-label block mb-2" htmlFor="sch-image">Immagine gioco (URL, facoltativa)</label>
              <div className="flex items-center gap-2">
                <input id="sch-image" type="text" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" className={inputCls} data-testid="sch-image-input" />
                <button type="button" onClick={() => setForm({ ...form, image: WARDOGS_IMG })} className="chamfer-sm border border-wardogs/50 text-wardogs hover:bg-wardogs hover:text-[#050505] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] whitespace-nowrap transition-colors duration-300" data-testid="sch-image-wardogs">
                  WARDOGS
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SaveButton label={editId ? "Salva modifica" : "Aggiungi al calendario"} busy={busy} testid="admin-schedule-save-button" />
            {editId && (
              <button type="button" onClick={() => { setEditId(null); setForm({ ...empty, date: selected }); }} className="chamfer-sm border border-[#2A2A2A] hover:border-valkyra text-steel hover:text-white font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-3.5 transition-colors duration-300" data-testid="admin-schedule-cancel-edit">
                Annulla
              </button>
            )}
          </div>
        </form>
      </Box>

      <Box title="Settimana automatica" testid="admin-schedule-week">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <p className="font-mono text-[10px] text-steel leading-relaxed max-w-2xl">
            Tutta la settimana da una schermata sola: attiva i giorni, imposta orario, gioco e immagine di ogni sessione
            (con "2× live" la pomeridiana ha titolo e immagine suoi). La bozza resta salvata in questo browser anche se chiudi la pagina.
            Quando salvi, la settimana viene riscritta esattamente come la vedi qui — i giorni spenti vengono svuotati.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => setWeekOffset((o) => o - 1)} className="chamfer-sm border border-[#2A2A2A] hover:border-wardogs text-steel hover:text-white px-4 py-2 font-mono text-sm transition-colors duration-300" data-testid="admin-week-prev">‹</button>
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white font-bold whitespace-nowrap" data-testid="admin-week-label">
              Settimana {fmtDay(weekMonday)} → {fmtDay(weekSunday)}
            </p>
            <button type="button" onClick={() => setWeekOffset((o) => o + 1)} className="chamfer-sm border border-[#2A2A2A] hover:border-wardogs text-steel hover:text-white px-4 py-2 font-mono text-sm transition-colors duration-300" data-testid="admin-week-next">›</button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-7 gap-3 mb-6" data-testid="admin-week-grid">
          {weekDays.map((d, i) => (
            <div key={d.date} className={`chamfer-sm border p-3.5 flex flex-col gap-2.5 transition-colors duration-300 ${d.on ? "border-wardogs/40 bg-[#0A0A0A]" : "border-[#1A1A1A] bg-[#050505] opacity-60"}`} data-testid={`admin-week-day-${i}`}>
              <button type="button" onClick={() => setDay(i, { on: !d.on })} className={`chamfer-sm px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.1em] font-bold transition-colors duration-300 ${d.on ? "bg-wardogs text-[#050505]" : "border border-[#2A2A2A] text-steel"}`} data-testid={`admin-week-toggle-${i}`}>
                {d.name.slice(0, 3)} {d.date.slice(8)}/{d.date.slice(5, 7)} {d.on ? "ON" : "OFF"}
              </button>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4A4D51]">Live serale</p>
              <input type="time" value={d.time} onChange={(e) => setDay(i, { time: e.target.value })} disabled={!d.on} className={inputCls} data-testid={`admin-week-time-${i}`} />
              <input type="text" value={d.title} onChange={(e) => setDay(i, { title: e.target.value })} disabled={!d.on} placeholder="Gioco / contenuto sera" className={inputCls} data-testid={`admin-week-title-${i}`} />
              <div className="flex items-center gap-1.5">
                <input type="text" value={d.image} onChange={(e) => setDay(i, { image: e.target.value })} disabled={!d.on} placeholder="URL immagine sera" className={inputCls} data-testid={`admin-week-image-${i}`} />
                <button type="button" onClick={() => setDay(i, { image: WARDOGS_IMG })} disabled={!d.on} className="chamfer-sm border border-wardogs/50 text-wardogs px-2 py-2 font-mono text-[9px] uppercase whitespace-nowrap" title="Logo WARDOGS" data-testid={`admin-week-wardogs-${i}`}>
                  WG
                </button>
              </div>
              <button type="button" onClick={() => setDay(i, { double: !d.double })} disabled={!d.on} title="Aggiungi una seconda live nello stesso giorno"
                className={`chamfer-sm px-3 py-2 font-mono text-[10px] uppercase tracking-[0.1em] font-bold transition-colors duration-300 ${d.double ? "bg-wardogs text-[#050505]" : "border border-[#2A2A2A] text-steel hover:text-white"}`} data-testid={`admin-week-double-${i}`}>
                2× live {d.double ? "ON" : "OFF"}
              </button>
              {d.double && (
                <div className="flex flex-col gap-2.5 border-t border-[#1A1A1A] pt-2.5" data-testid={`admin-week-session2-${i}`}>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-wardogs">Live pomeridiana</p>
                  <input type="time" value={d.time2} onChange={(e) => setDay(i, { time2: e.target.value })} disabled={!d.on} className={inputCls} data-testid={`admin-week-time2-${i}`} />
                  <input type="text" value={d.title2} onChange={(e) => setDay(i, { title2: e.target.value })} disabled={!d.on} placeholder="Gioco / contenuto pomeriggio" className={inputCls} data-testid={`admin-week-title2-${i}`} />
                  <div className="flex items-center gap-1.5">
                    <input type="text" value={d.image2} onChange={(e) => setDay(i, { image2: e.target.value })} disabled={!d.on} placeholder="URL immagine pomeriggio" className={inputCls} data-testid={`admin-week-image2-${i}`} />
                    <button type="button" onClick={() => setDay(i, { image2: WARDOGS_IMG })} disabled={!d.on} className="chamfer-sm border border-wardogs/50 text-wardogs px-2 py-2 font-mono text-[9px] uppercase whitespace-nowrap" title="Logo WARDOGS" data-testid={`admin-week-wardogs2-${i}`}>
                      WG
                    </button>
                  </div>
                </div>
              )}
              <select value={d.kind} onChange={(e) => setDay(i, { kind: e.target.value, color: "" })} disabled={!d.on} className={inputCls} data-testid={`admin-week-kind-${i}`}>
                {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              <Swatches value={d.color} kindId={d.kind} onPick={(c) => setDay(i, { color: c })} prefix={`admin-week-color-${i}`} />
            </div>
          ))}
        </div>
        <button type="button" onClick={saveWeek} disabled={weekBusy} className="chamfer-sm bg-wardogs hover:bg-wardogs-hover disabled:opacity-50 text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300 flex items-center gap-2" data-testid="admin-week-save-button">
          <Plus className="w-4 h-4" /> Salva la settimana ({weekDays.reduce((n, d) => n + (d.on ? (d.double && d.time2 !== d.time ? 2 : 1) : 0), 0)} live)
        </button>
      </Box>
    </>
  );
}

function MediaTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: "image", url: "", title: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/media").then(({ data }) => setItems(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/media", form, { headers: headers() });
      toast.success("Media aggiunto alla homepage.");
      setForm({ type: "image", url: "", title: "" });
      load();
    } catch (err) {
      toast.error(err403(err) ? "Chiave admin non valida." : "Errore nell'aggiunta.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/media/${id}`, { headers: headers() });
      toast.success("Media rimosso.");
      load();
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };

  return (
    <>
      <Box title="Aggiungi foto o video alla homepage" testid="admin-media-form">
        <form onSubmit={add}>
          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <div>
              <label className="hud-label block mb-2" htmlFor="media-type">Tipo</label>
              <select id="media-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="media-type-input" className="w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300">
                <option value="image">Foto (URL immagine)</option>
                <option value="youtube">Video YouTube (link)</option>
              </select>
            </div>
            <Field label="URL" id="media-url" value={form.url} onChange={(v) => setForm({ ...form, url: v })} required placeholder={form.type === "youtube" ? "https://www.youtube.com/watch?v=…" : "https://…/foto.jpg"} />
            <Field label="Titolo (opzionale)" id="media-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Didascalia…" />
          </div>
          <SaveButton label="Aggiungi media" busy={busy} testid="admin-media-create-button" />
        </form>
      </Box>
      <p className="hud-label mb-4">Media pubblicati ({items.length})</p>
      <div className="flex flex-col gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
        {items.map((m) => (
          <RowItem key={m.id} testid={`admin-media-${m.id}`} title={m.title || m.url} sub={m.type === "youtube" ? `YouTube: ${m.video_id}` : m.url} onRemove={() => remove(m.id)} />
        ))}
      </div>
    </>
  );
}

// Contatore click sponsor LagoFast (totale, per fonte, ultimi 14 giorni)
function SponsorStatsBox() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/sponsor-stats", { headers: headers() }).then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!stats) return null;
  const days = Object.entries(stats.days || {}).slice(-14);
  const max = Math.max(1, ...days.map(([, v]) => v));
  const SRC_LABELS = { cta: "Pulsante pagina", banner: "Banner pagina", home_strip: "Striscia home" };
  return (
    <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-5 mb-4" data-testid="sponsor-stats-box">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 mb-4">
        <span className="font-display text-4xl text-wardogs" data-testid="sponsor-stats-total">{stats.total}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">click totali sul link partner</span>
        {(Object.entries(stats.by_source || {})).map(([k, v]) => (
          <span key={k} className="font-mono text-[10px] uppercase tracking-[0.15em] text-white" data-testid={`sponsor-stats-src-${k}`}>
            {SRC_LABELS[k] || k}: <b className="text-wardogs">{v}</b>
          </span>
        ))}
      </div>
      {days.length > 0 && (
        <div className="flex items-end gap-1 h-14" data-testid="sponsor-stats-chart">
          {days.map(([day, v]) => (
            <div key={day} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${day}: ${v} click`}>
              <div className="w-full bg-wardogs/70" style={{ height: `${Math.max(6, (v / max) * 44)}px` }} />
              <span className="font-mono text-[8px] text-steel">{day.slice(8)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SubHead = ({ children }) => (

  <p className="hud-label text-wardogs mt-8 mb-4 pt-6 border-t border-[#1A1A1A]">{children}</p>
);

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function TestiTab() {
  const KEYS = [
    ["hero.line1", "Hero — riga 1", "Il titolo gigante in cima alla homepage, prima riga in bianco (ora \"NATI PER\")"],
    ["hero.line2", "Hero — riga 2 (arancione)", "Seconda riga del titolo gigante, quella colorata in arancione (ora \"PASSIONE.\")"],
    ["hero.line3", "Hero — riga 3", "Terza riga del titolo gigante, in grigio (ora \"MAI PER OBBLIGO.\")"],
    ["hero.sub", "Hero — sottotitolo", "La frase piccola sotto il titolo gigante della homepage (ora il racconto del progetto: nati dalla passione per WARDOGS, non un clan, community over 30)"],
  ];
  const GUIDE_KEYS = [
    ["g1", "01 — La battaglia"],
    ["g2", "02 — Zona di controllo"],
    ["g3", "03 — Hot zone"],
    ["g4", "04 — Le torri"],
    ["g5", "05 — Audio di guerra"],
    ["g6", "06 — Squadre e voci"],
    ["g7", "07 — Libertà totale"],
    ["g8", "08 — Progressione meritata"],
    ["g9", "09 — Non è COD, non è Battlefield"],
    ["g10", "10 — Early access onesta"],
  ];
  const [texts, setTexts] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/settings", { headers: headers() }).then(({ data }) => {
      setTexts(data.texts || {});
    }).catch(() => toast.error("Chiave admin non valida o non salvata."));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings", { texts }, { headers: headers() });
      toast.success("Testi aggiornati — online all'istante, nessun deploy necessario.");
    } catch {
      toast.error("Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box title="Testi del sito — homepage e guida (IT / EN)" testid="admin-texts-form">
      <form onSubmit={save}>
        <SubHead>Testi della homepage (IT / EN)</SubHead>
        <p className="font-mono text-[10px] text-steel mb-5 leading-relaxed">Questi sono i testi principali che i visitatori vedono appena aprono il sito. Editor libero stile Discord: <span className="text-white">**grassetto**</span>, <span className="text-white">*corsivo*</span>, <span className="text-white">[testo](https://link)</span>, colori dal selettore (seleziona col mouse, anche più colori nello stesso testo), doppio invio per una riga di spazio. <span className="text-wardogs">Lascia vuoto per NASCONDERE la riga dal sito</span> — non torna più il testo di default.</p>
        {KEYS.map(([key, label, desc]) => (
          <div key={key} className="grid md:grid-cols-2 gap-4 mb-4">
            <RichTextEditor label={`${label} — IT`} id={`text-${key}-it`} value={texts[key]?.it || ""} onChange={(v) => setTexts({ ...texts, [key]: { ...(texts[key] || {}), it: v } })} compact={key !== "hero.sub"} hint={desc} />
            <RichTextEditor label={`${label} — EN`} id={`text-${key}-en`} value={texts[key]?.en || ""} onChange={(v) => setTexts({ ...texts, [key]: { ...(texts[key] || {}), en: v } })} compact={key !== "hero.sub"} hint="Stessa riga, versione inglese per chi visita il sito in EN" />
          </div>
        ))}

        <SubHead>Guida al gioco — le 10 schede (IT / EN)</SubHead>
        <p className="font-mono text-[10px] text-steel mb-4 leading-relaxed">Queste sono le 10 schede della sezione "Come si gioca" nella homepage, in ordine da sinistra a destra. Ogni scheda ha un titolo in grande e un testo sotto. Le modifiche qui vanno online all'istante, senza deploy. <span className="text-wardogs">Lascia vuoto per NASCONDERE il testo dal sito</span>; il riquadro scorre con la rotellina quando scrivi tanto.</p>
        {GUIDE_KEYS.map(([g, label]) => (
          <div key={g} className="mb-5">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <RichTextEditor label={`${label} — titolo IT`} id={`text-${g}.title-it`} value={texts[`${g}.title`]?.it || ""} onChange={(v) => setTexts({ ...texts, [`${g}.title`]: { ...(texts[`${g}.title`] || {}), it: v } })} compact />
              <RichTextEditor label={`${label} — titolo EN`} id={`text-${g}.title-en`} value={texts[`${g}.title`]?.en || ""} onChange={(v) => setTexts({ ...texts, [`${g}.title`]: { ...(texts[`${g}.title`] || {}), en: v } })} compact />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <RichTextEditor label={`${label} — testo IT`} id={`text-${g}.text-it`} value={texts[`${g}.text`]?.it || ""} onChange={(v) => setTexts({ ...texts, [`${g}.text`]: { ...(texts[`${g}.text`] || {}), it: v } })} />
              <RichTextEditor label={`${label} — testo EN`} id={`text-${g}.text-en`} value={texts[`${g}.text`]?.en || ""} onChange={(v) => setTexts({ ...texts, [`${g}.text`]: { ...(texts[`${g}.text`] || {}), en: v } })} />
            </div>
          </div>
        ))}

        <SubHead>Post pubblicitario — pagina /pubblicita (IT / EN)</SubHead>
        <p className="font-mono text-[10px] text-steel mb-4 leading-relaxed">Il testo del post social pronto da copiare nella pagina Pubblicità. Quando lo cambi qui, la pagina si aggiorna da sola — e con lei l'immagine di anteprima che appare quando condividi il link sui social. <span className="text-wardogs">Lascia vuoto per NASCONDERE il testo dalla pagina</span> (per riavere il testo ufficiale basta copiarlo dalla versione attuale prima di svuotare).</p>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <RichTextEditor label="Post completo — IT" id="text-pub.post_full-it" value={texts["pub.post_full"]?.it || ""} onChange={(v) => setTexts({ ...texts, "pub.post_full": { ...(texts["pub.post_full"] || {}), it: v } })} hint="Versione lunga per Facebook, Discord, Telegram e forum" />
          <RichTextEditor label="Post completo — EN" id="text-pub.post_full-en" value={texts["pub.post_full"]?.en || ""} onChange={(v) => setTexts({ ...texts, "pub.post_full": { ...(texts["pub.post_full"] || {}), en: v } })} hint="Stesso post, versione inglese" />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <RichTextEditor label="Versione corta (X/Twitter) — IT" id="text-pub.post_short-it" value={texts["pub.post_short"]?.it || ""} onChange={(v) => setTexts({ ...texts, "pub.post_short": { ...(texts["pub.post_short"] || {}), it: v } })} compact hint="Deve stare nei 280 caratteri di X" />
          <RichTextEditor label="Versione corta (X/Twitter) — EN" id="text-pub.post_short-en" value={texts["pub.post_short"]?.en || ""} onChange={(v) => setTexts({ ...texts, "pub.post_short": { ...(texts["pub.post_short"] || {}), en: v } })} compact hint="Must fit X's 280 characters" />
        </div>

        <SubHead>Pagina LagoFast — sponsor (IT / EN)</SubHead>
        <SponsorStatsBox />
        <p className="font-mono text-[10px] text-steel mb-4 leading-relaxed">Il testo dello sponsor mostrato nella pagina LagoFast del NAV. Editor libero: colori, grassetto, link, righe di spazio. Il campo "Link sponsor" attiva il pulsante in fondo alla pagina (lascialo vuoto finché non hai l'URL).</p>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <RichTextEditor label="Testo sponsor — IT" id="text-lago.text-it" value={texts["lago.text"]?.it || ""} onChange={(v) => setTexts({ ...texts, "lago.text": { ...(texts["lago.text"] || {}), it: v } })} />
          <RichTextEditor label="Testo sponsor — EN" id="text-lago.text-en" value={texts["lago.text"]?.en || ""} onChange={(v) => setTexts({ ...texts, "lago.text": { ...(texts["lago.text"] || {}), en: v } })} />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <RichTextEditor label="Link sponsor (URL) — IT" id="text-lago.url-it" value={texts["lago.url"]?.it || ""} onChange={(v) => setTexts({ ...texts, "lago.url": { ...(texts["lago.url"] || {}), it: v } })} compact hint="Es. https://lagofast.com/... — apre il pulsante Scopri LagoFast" />
          <RichTextEditor label="Link sponsor (URL) — EN" id="text-lago.url-en" value={texts["lago.url"]?.en || ""} onChange={(v) => setTexts({ ...texts, "lago.url": { ...(texts["lago.url"] || {}), en: v } })} compact hint="Same sponsor link for EN visitors" />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <RichTextEditor label="Codice sconto — IT" id="text-lago.code-it" value={texts["lago.code"]?.it || ""} onChange={(v) => setTexts({ ...texts, "lago.code": { ...(texts["lago.code"] || {}), it: v } })} compact hint="Es. ILMASTRO — mostrato nella card arancione con pulsante copia (vuoto = nascosta)" />
          <RichTextEditor label="Codice sconto — EN" id="text-lago.code-en" value={texts["lago.code"]?.en || ""} onChange={(v) => setTexts({ ...texts, "lago.code": { ...(texts["lago.code"] || {}), en: v } })} compact hint="Same discount code for EN visitors" />
        </div>

        <div className="flex flex-wrap gap-3 pt-6 border-t border-[#1A1A1A]">
          <SaveButton label="Salva impostazioni" busy={busy} testid="admin-texts-save-button" />
        </div>
      </form>
    </Box>
  );
}

function PayPalTab() {
  const [paypal, setPaypal] = useState("");
  const [busy, setBusy] = useState(false);
  const [donations, setDonations] = useState(null);
  const [annName, setAnnName] = useState("");
  const [annAmount, setAnnAmount] = useState("");
  const [annBusy, setAnnBusy] = useState(false);
  const loadDonations = () => api.get("/admin/donations", { headers: headers() }).then(({ data }) => setDonations(data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/settings", { headers: headers() }).then(({ data }) => setPaypal(data.paypal_url || "")).catch(() => toast.error("Chiave admin non valida o non salvata."));
    loadDonations();
  }, []);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings", { paypal_url: paypal }, { headers: headers() });
      toast.success("Link PayPal salvato.");
    } catch {
      toast.error("Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };
  // Annuncio manuale donazione colletta: le pool PayPal non notificano il sito,
  // quindi il ringraziamento pubblico parte da qui (canale sostenitori + muro donatori)
  const announce = async (e) => {
    e.preventDefault();
    if (annBusy || !annAmount.trim()) return;
    setAnnBusy(true);
    try {
      const { data } = await api.post("/admin/announce-donation", { name: annName.trim() || "Anonimo", amount: annAmount.trim(), currency: "EUR" }, { headers: headers() });
      if (data.posted_discord) toast.success("Ringraziamento pubblicato su Discord e aggiunto al muro!");
      else toast.success("Aggiunto al muro (Discord non raggiungibile).");
      setAnnName(""); setAnnAmount("");
      loadDonations();
    } catch {
      toast.error("Annuncio non riuscito — controlla la chiave admin.");
    } finally {
      setAnnBusy(false);
    }
  };
  const fmtD = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
  const medals = ["text-wardogs", "text-[#C0C0C0]", "text-[#CD7F32]"];
  return (
    <>
      <Box title="Donazioni PayPal" testid="admin-paypal-form">
        <form onSubmit={save}>
          <div className="mb-5">
            <Field label="Link colletta PayPal" id="paypal-url" value={paypal} onChange={setPaypal} placeholder="https://www.paypal.com/pool/..." hint="E' il pulsante principale della pagina Donazione: i soldi arrivano alla colletta (pool) PayPal." />
          </div>
          <SaveButton label="Salva link colletta" busy={busy} testid="admin-paypal-save-button" />
        </form>
      </Box>
      <Box title="Annuncia donazione colletta — ringraziamento su Discord + muro" testid="admin-announce-donation">
        <p className="font-mono text-[11px] text-steel leading-relaxed mb-4">
          Le collette PayPal non avvisano il sito in automatico: quando arriva la mail di PayPal, annuncia qui la donazione —
          parte il ringraziamento pubblico nel canale sostenitori e il nome entra nel Muro dei Donatori.
        </p>
        <form onSubmit={announce} className="flex flex-wrap gap-2 items-center">
          <input value={annName} onChange={(e) => setAnnName(e.target.value)} placeholder="Nome (o Anonimo)" data-testid="announce-donation-name"
            className="flex-1 min-w-40 bg-[#050505] border border-[#1A1A1A] px-3 py-2 font-mono text-xs text-white focus:border-wardogs outline-none" />
          <input value={annAmount} onChange={(e) => setAnnAmount(e.target.value.replace(/[^0-9.,]/g, "").replace(",", "."))} placeholder="Importo €" data-testid="announce-donation-amount"
            className="w-28 bg-[#050505] border border-[#1A1A1A] px-3 py-2 font-mono text-xs text-white focus:border-wardogs outline-none" />
          <button type="submit" disabled={annBusy} data-testid="announce-donation-button"
            className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300 disabled:opacity-50">
            {annBusy ? "..." : "Annuncia e ringrazia"}
          </button>
        </form>
      </Box>
      <Box title="Registro donazioni — chi, quanto, quando" testid="admin-donations">
        {!donations ? (
          <p className="font-mono text-xs text-steel">Caricamento…</p>
        ) : donations.count === 0 ? (
          <p className="font-mono text-xs text-steel" data-testid="admin-donations-empty">Nessuna donazione ricevuta finora. Quando qualcuno dona dalla pagina Donazione, appare qui entro pochi secondi.</p>
        ) : (
          <>
            <p className="font-mono text-xs text-steel mb-4" data-testid="admin-donations-summary">{donations.count} donazioni · totale {donations.total_eur} € (conteggio solo EUR)</p>
            <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto pr-1" data-lenis-prevent data-testid="admin-donations-list">
              {donations.donations.map((d, i) => (
                <div key={d.order_id} className={`chamfer-sm border px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1 ${i === 0 ? "border-wardogs/50 bg-wardogs/10 donor-glow" : "border-[#1A1A1A] bg-[#050505]"}`} data-testid={`admin-donation-${i}`}>
                  <span className={`font-display text-2xl w-8 ${medals[0] ? "" : ""} ${i === 0 ? "text-wardogs" : "text-[#2A2A2A]"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-mono text-xs text-white font-bold">{d.payer_name}</span>
                  {d.donor_login && <span className="font-mono text-[10px] text-steel">({d.donor_login})</span>}
                  <span className="font-mono text-sm text-wardogs font-bold">{d.amount} {d.currency}</span>
                  <span className="font-mono text-[9px] text-[#4A4D51] ml-auto">{fmtD(d.at)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </Box>
    </>
  );
}

function WebhookTab() {
  const [webhook, setWebhook] = useState("");
  const [pingRole, setPingRole] = useState("");
  const [announceYt, setAnnounceYt] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/settings", { headers: headers() }).then(({ data }) => {
      setWebhook(data.discord_webhook || "");
      setPingRole(data.discord_ping_role_id || "");
      setAnnounceYt(!!data.announce_yt_enabled);
    }).catch(() => toast.error("Chiave admin non valida o non salvata."));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings", {
        discord_webhook: webhook,
        discord_ping_role_id: pingRole, announce_yt_enabled: announceYt,
      }, { headers: headers() });
      toast.success("Webhook salvati.");
    } catch (err) {
      toast.error(err?.response?.data?.detail === "invalid_webhook" ? "Il webhook deve iniziare con https://discord.com/api/webhooks/" : "Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };
  const testAnnounce = async (kind = "live") => {
    try {
      await api.post(`/admin/announce-test?kind=${kind}`, {}, { headers: headers() });
      toast.success(kind === "youtube" ? "Annuncio video di test inviato su Discord (ultimo video BULKHEAD)." : "Annuncio di test inviato su Discord.");
    } catch (err) {
      toast.error(err?.response?.data?.detail === "no_webhook" ? "Salva prima il webhook Discord." : "Errore nell'invio.");
    }
  };
  return (
    <Box title="Webhook Discord — annunci automatici" testid="admin-webhook-form">
      <form onSubmit={save}>
        <SubHead>Annuncio quando vai in live su Twitch</SubHead>
        <div className="mb-4">
          <Field label="Webhook Discord (live)" id="discord-webhook" value={webhook} onChange={setWebhook} placeholder="https://discord.com/api/webhooks/…" />
          <p className="font-mono text-[10px] text-steel mt-2">Il webhook pubblica solo nel canale dove viene creato: crealo dal canale annunci-live. Il messaggio include il player della live cliccabile.</p>
        </div>
        <div className="mb-4">
          <Field label="ID ruolo Discord da taggare (es. Ping Twitch Live)" id="discord-ping-role" value={pingRole} onChange={setPingRole} placeholder="1234567890123456789" />
          <p className="font-mono text-[10px] text-steel mt-2">Impostazioni server → Ruoli → tasto destro sul ruolo → Copia ID ruolo (serve la modalità sviluppatore attiva). Il ruolo deve avere "Consenti a chiunque di menzionare questo ruolo" attivo. Vuoto = nessuna menzione.</p>
        </div>
        <button type="button" onClick={() => testAnnounce("live")} data-testid="admin-announce-test-button" className="chamfer-sm border border-[#5865F2] text-[#5865F2] hover:bg-[#5865F2] hover:text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300">
          Test annuncio live
        </button>

        <SubHead>Annunci nuovi video BULKHEAD (canale chat)</SubHead>
        <p className="font-mono text-[10px] text-steel mb-4">Ogni nuovo video del canale BULKHEAD viene annunciato entro 15 minuti nel canale chat del server Discord tramite il bot, con anteprima e link al sito. Nessun webhook da configurare.</p>
        <label className="flex items-center gap-3 mb-3 cursor-pointer w-fit" data-testid="admin-announce-yt-toggle">
          <input type="checkbox" checked={announceYt} onChange={(e) => setAnnounceYt(e.target.checked)} className="w-4 h-4 accent-[#FF6A00]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white">Annunci automatici attivi</span>
        </label>
        <p className="font-mono text-[10px] text-valkyra mb-4">Attivalo SOLO sul sito live: se lo attivi sia in anteprima che in produzione i messaggi arriveranno doppi. Ricordati di salvare.</p>
        <button type="button" onClick={() => testAnnounce("youtube")} data-testid="admin-announce-youtube-button" className="chamfer-sm border border-[#c4302b] text-[#c4302b] hover:bg-[#c4302b] hover:text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300">
          Test annuncio video
        </button>

        <div className="flex flex-wrap gap-3 pt-6 border-t border-[#1A1A1A]">
          <SaveButton label="Salva webhook" busy={busy} testid="admin-webhook-save-button" />
        </div>
      </form>
    </Box>
  );
}

function CountdownTab() {
  const [countdownTo, setCountdownTo] = useState("");
  const [countdownLabel, setCountdownLabel] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/settings", { headers: headers() }).then(({ data }) => {
      setCountdownTo(toLocalInput(data.countdown_to));
      setCountdownLabel(data.countdown_label || "");
    }).catch(() => toast.error("Chiave admin non valida o non salvata."));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings", {
        countdown_to: countdownTo ? new Date(countdownTo).toISOString() : "",
        countdown_label: countdownLabel,
      }, { headers: headers() });
      toast.success("Countdown OBS salvato.");
    } catch {
      toast.error("Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };
  const cdUrl = countdownTo
    ? `${window.location.origin}/countdown?to=${encodeURIComponent(new Date(countdownTo).toISOString())}${countdownLabel ? `&label=${encodeURIComponent(countdownLabel)}` : ""}`
    : `${window.location.origin}/countdown`;
  const copyCd = async () => {
    try {
      await navigator.clipboard.writeText(cdUrl);
      toast.success("URL copiato — in OBS: Aggiungi sorgente → Browser → incolla (consigliato 1280×300).");
    } catch {
      toast.error("Copia non riuscita: seleziona e copia manualmente il link.");
    }
  };
  return (
    <Box title="Countdown per OBS / StreamElements (libero)" testid="admin-countdown-form">
      <form onSubmit={save}>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <Field label="Data e ora del countdown" id="countdown-to" type="datetime-local" value={countdownTo} onChange={setCountdownTo} />
          <Field label="Etichetta sopra il countdown (lascia vuoto per mostrare solo i numeri)" id="countdown-label" value={countdownLabel} onChange={setCountdownLabel} placeholder="WARDOGS — EARLY ACCESS" />
        </div>
        <div className="mb-2 chamfer-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3 flex items-center gap-3">
          <code className="font-mono text-[10px] text-steel truncate flex-1" data-testid="admin-countdown-url">{cdUrl}</code>
          <button type="button" onClick={copyCd} data-testid="admin-countdown-copy" className="chamfer-sm border border-wardogs/50 text-wardogs font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 shrink-0 hover:bg-wardogs hover:text-[#050505] transition-colors duration-300">
            Copia URL
          </button>
        </div>
        <p className="font-mono text-[10px] text-steel mb-4">Questo countdown è SOLO per le tue live: salva, copia l'URL e incollalo in OBS come Browser Source (1280×300). Sfondo trasparente di default — aggiungi &bg=dark per lo sfondo scuro. La barra in cima al sito invece è fissa sulla data di uscita di WARDOGS (10 settembre 2026) e sparisce da sola al lancio — non devi toccare nulla.</p>
        <SaveButton label="Salva countdown" busy={busy} testid="admin-countdown-save-button" />
      </form>
    </Box>
  );
}

function YouTubeTab() {
  const [ytChannel, setYtChannel] = useState("");
  const [channels, setChannels] = useState([]);
  const [newChannel, setNewChannel] = useState({ channel_id: "", label: "" });
  const [busy, setBusy] = useState(false);
  const loadChannels = () => api.get("/youtube/channels", { headers: headers() }).then(({ data }) => setChannels(data.extra_channels || [])).catch(() => {});
  useEffect(() => {
    api.get("/admin/settings", { headers: headers() }).then(({ data }) => setYtChannel(data.youtube_channel_id || "")).catch(() => toast.error("Chiave admin non valida o non salvata."));
    loadChannels();
  }, []);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings", { youtube_channel_id: ytChannel }, { headers: headers() });
      toast.success("Canale YouTube salvato.");
    } catch {
      toast.error("Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };
  const addChannel = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/youtube/channels", newChannel, { headers: headers() });
      toast.success(`Canale aggiunto — trovati ${data.videos_found} video.`);
      setNewChannel({ channel_id: "", label: "" });
      loadChannels();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d === "channel_not_found" ? "Canale non trovato: controlla l'ID (deve iniziare con UC…)." : "Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };
  const removeChannel = async (cid) => {
    try {
      await api.delete(`/youtube/channels/${cid}`, { headers: headers() });
      toast.success("Canale rimosso.");
      loadChannels();
    } catch {
      toast.error("Chiave admin non valida.");
    }
  };
  return (
    <>
      <Box title="YouTube — canale BULKHEAD (principale)" testid="admin-youtube-form">
        <form onSubmit={save}>
          <div className="mb-5">
            <Field label="ID canale YouTube (override opzionale)" id="yt-channel" value={ytChannel} onChange={setYtChannel} placeholder="UCxxxxxxxxxxxxxxxx" hint="Lascia vuoto per usare il canale predefinito. Cambialo solo se BULKHEAD cambia canale ufficiale: video e annunci si aggiornano da soli." />
          </div>
          <SaveButton label="Salva canale" busy={busy} testid="admin-youtube-save-button" />
        </form>
      </Box>
      <Box title="Canali aggiuntivi — archivio e annunci automatici" testid="admin-youtube-extra">
        <p className="font-mono text-[10px] text-steel mb-5 leading-relaxed">I video di questi canali entrano automaticamente nell'archivio YouTube del sito insieme a quelli del canale principale, e le nuove uscite vengono annunciate su Discord come sempre. Serve l'ID canale (inizia con UC…): lo trovi sulla pagina del canale → Informazioni → Condividi canale → Copia ID canale.</p>
        <form onSubmit={addChannel} className="grid md:grid-cols-[1fr_1fr_auto] gap-4 mb-6 items-end">
          <Field label="ID canale YouTube" id="yt-extra-id" value={newChannel.channel_id} onChange={(v) => setNewChannel({ ...newChannel, channel_id: v })} required placeholder="UCxxxxxxxxxxxxxxxx" />
          <Field label="Nome da mostrare (opzionale)" id="yt-extra-label" value={newChannel.label} onChange={(v) => setNewChannel({ ...newChannel, label: v })} placeholder="es. Team17" />
          <SaveButton label="Aggiungi" busy={busy} testid="admin-youtube-add-channel" />
        </form>
        {channels.length === 0 ? (
          <p className="font-mono text-[11px] text-[#4A4D51]" data-testid="yt-extra-empty">Nessun canale aggiuntivo — attivo solo il canale principale.</p>
        ) : (
          <div className="flex flex-col gap-2" data-testid="yt-extra-list">
            {channels.map((c) => (
              <div key={c.channel_id} className="chamfer-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3 flex items-center gap-3" data-testid={`yt-extra-${c.channel_id}`}>
                <Youtube className="w-4 h-4 text-[#FF0000] shrink-0" />
                <span className="font-mono text-xs text-white">{c.label || c.channel_id}</span>
                <span className="font-mono text-[10px] text-[#4A4D51]">{c.channel_id}</span>
                <button onClick={() => removeChannel(c.channel_id)} className="ml-auto text-[#4A4D51] hover:text-valkyra transition-colors duration-300" data-testid={`yt-extra-del-${c.channel_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Box>
    </>
  );
}

function SitoTab() {
  const [logoUrl, setLogoUrl] = useState("");
  const [publicUrl, setPublicUrl] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/settings", { headers: headers() }).then(({ data }) => {
      setLogoUrl(data.logo_url || "");
      setPublicUrl(data.public_site_url || "");
    }).catch(() => toast.error("Chiave admin non valida o non salvata."));
  }, []);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings", { logo_url: logoUrl, public_site_url: publicUrl }, { headers: headers() });
      toast.success("Impostazioni sito salvate.");
    } catch (err) {
      toast.error(err?.response?.data?.detail === "invalid_site_url" ? "Il dominio deve iniziare con https:// o http://" : "Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box title="Logo e dominio pubblico del sito" testid="admin-sito-form">
      <form onSubmit={save}>
        <div className="mb-5">
          <Field label="URL logo del sito (in alto a sinistra) — lascia vuoto per il logo WARDOGS ufficiale" id="logo-url" value={logoUrl} onChange={setLogoUrl} placeholder="https://…/logo.png" />
          <p className="font-mono text-[10px] text-steel mt-2">Consiglio: immagine PNG quadrata con sfondo trasparente. Dopo il salvataggio ricarica la pagina per vederlo.</p>
        </div>
        <div className="mb-5">
          <Field label="URL pubblico del sito (usato nei link degli annunci Discord)" id="public-site-url" value={publicUrl} onChange={setPublicUrl} placeholder="https://www.wardogsitaliangamer.it" />
          <p className="font-mono text-[10px] text-steel mt-2">Il dominio ufficiale che gira su Discord. Senza www se preferisci. Se vuoto, viene usato l'indirizzo tecnico dell'ambiente (anteprima o produzione).</p>
        </div>
        <SaveButton label="Salva logo e dominio" busy={busy} testid="admin-sito-save-button" />
      </form>
    </Box>
  );
}

function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [newLogin, setNewLogin] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/settings", { headers: headers() }).then(({ data }) => {
    const raw = data.staff || [];
    setStaff(raw.map((s) => (typeof s === "string" ? { login: s, tabs: null } : { login: s.login, tabs: s.tabs ?? null })));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const ALL_TABS = TAB_GROUPS.flatMap((g) => g.items.map((i) => ({ id: i.id, label: i.label })));

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/settings", { staff }, { headers: headers() });
      toast.success("Staff aggiornato: i permessi sono attivi subito.");
      load();
    } catch {
      toast.error("Chiave admin non valida.");
    } finally {
      setBusy(false);
    }
  };
  const addRow = () => {
    const v = newLogin.trim().toLowerCase();
    if (!v || staff.some((s) => s.login === v)) return;
    setStaff([...staff, { login: v, tabs: null }]);
    setNewLogin("");
  };
  const toggleTab = (login, tabId) => {
    setStaff(staff.map((s) => {
      if (s.login !== login || s.tabs === null) return s;
      const has = s.tabs.includes(tabId);
      return { ...s, tabs: has ? s.tabs.filter((t) => t !== tabId) : [...s.tabs, tabId] };
    }));
  };
  const setFull = (login, full) => {
    setStaff(staff.map((s) => (s.login === login ? { ...s, tabs: full ? null : [] } : s)));
  };

  return (
    <Box title="Membri staff e permessi" testid="admin-staff-form">
      <p className="font-mono text-[10px] text-steel mb-5 leading-relaxed">
        Ogni membro accede alla sezione Staff col proprio account, senza chiave admin. Scegli cosa vede: <span className="text-white">Accesso completo</span> oppure solo le sezioni che spunti. Le modifiche sono immediate; il tuo account resta sempre con accesso completo.
      </p>
      <div className="flex flex-col gap-4 mb-6">
        {staff.map((s) => (
          <div key={s.login} className="chamfer-sm border border-[#1A1A1A] bg-[#050505] p-4" data-testid={`staff-member-${s.login}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-display text-xl uppercase">{s.login}</span>
              {s.login === "ilquartiermastro" ? (
                <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-wardogs">Fondatore — accesso completo sempre</span>
              ) : (
                <>
                  <button
                    onClick={() => setFull(s.login, true)}
                    className={`chamfer-sm px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] font-bold transition-colors duration-300 ${s.tabs === null ? "bg-wardogs text-[#050505]" : "border border-[#2A2A2A] text-steel hover:text-white"}`}
                    data-testid={`staff-full-${s.login}`}
                  >
                    Accesso completo
                  </button>
                  <button
                    onClick={() => setFull(s.login, false)}
                    className={`chamfer-sm px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] font-bold transition-colors duration-300 ${s.tabs !== null ? "bg-wardogs text-[#050505]" : "border border-[#2A2A2A] text-steel hover:text-white"}`}
                    data-testid={`staff-custom-${s.login}`}
                  >
                    Solo sezioni scelte
                  </button>
                  <button onClick={() => setStaff(staff.filter((x) => x.login !== s.login))} data-testid={`staff-remove-${s.login}`} className="ml-auto text-steel hover:text-valkyra transition-colors duration-300" aria-label={`Rimuovi ${s.login}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            {s.tabs !== null && s.login !== "ilquartiermastro" && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 mt-3" data-testid={`staff-tabs-${s.login}`}>
                {ALL_TABS.map((t) => (
                  <label key={t.id} className={`flex items-center gap-2 px-2.5 py-2 chamfer-sm border cursor-pointer transition-colors duration-300 ${s.tabs.includes(t.id) ? "border-wardogs/50 bg-wardogs/5 text-white" : "border-[#141414] text-steel"}`} data-testid={`staff-tab-${s.login}-${t.id}`}>
                    <input type="checkbox" checked={s.tabs.includes(t.id)} onChange={() => toggleTab(s.login, t.id)} className="accent-[#FF5C00]" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.1em]">{t.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-3 mb-5">
        <input
          value={newLogin}
          onChange={(e) => setNewLogin(e.target.value)}
          placeholder="Login Twitch del membro"
          className="flex-1 bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300"
          data-testid="staff-login-input"
        />
        <button onClick={addRow} className="chamfer-sm border border-wardogs text-wardogs hover:bg-wardogs hover:text-[#050505] px-5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-300" data-testid="staff-add-button">
          + Aggiungi
        </button>
      </div>
      <SaveButton label="Salva staff e permessi" busy={busy} testid="admin-staff-save-button" onClick={save} />
    </Box>
  );
}

function UtentiTab() {
  const [login, setLogin] = useState("");
  const [role, setRole] = useState("lonestar");
  const [busy, setBusy] = useState(false);
  const [donorLogin, setDonorLogin] = useState("");
  const [donorBusy, setDonorBusy] = useState(false);

  const submitDonor = async (e) => {
    e.preventDefault();
    if (donorBusy) return;
    setDonorBusy(true);
    try {
      const { data } = await api.post("/admin/set-donor", { login: donorLogin }, { headers: headers() });
      if (data.discord_role === "ok") {
        toast.success(`${data.login} segnato come donatore: badge dorato sul sito + ruolo "Donatori" assegnato su Discord.`);
      } else if (data.discord_role === "no_discord") {
        toast.success(`${data.login} segnato come donatore (badge dorato attivo).`);
        toast.error("Ruolo Discord NON assegnato: l'utente non ha collegato il suo account Discord al sito.");
      } else {
        toast.success(`${data.login} segnato come donatore (badge dorato attivo).`);
        toast.error("Ruolo Discord non assegnato: riprova tra poco o assegnalo a mano sul server.");
      }
      setDonorLogin("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "user_not_found") toast.error("Utente non trovato: controlla il nome esatto con cui ha fatto login.");
      else if (err403(err)) toast.error("Chiave admin non valida.");
      else toast.error("Errore durante il salvataggio.");
    } finally {
      setDonorBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/set-role", { login, role }, { headers: headers() });
      toast.success(`Fazione di ${data.login} cambiata in ${data.role.toUpperCase()} — per loro riparte il blocco di 30 giorni.`);
      setLogin("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "user_not_found") toast.error("Utente non trovato: controlla il nome esatto con cui ha fatto login.");
      else if (err403(err)) toast.error("Chiave admin non valida.");
      else toast.error("Errore durante il cambio fazione.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Box title="Cambia la fazione di un utente (bypassa i 30 giorni — il blocco riparte dal tuo cambio)" testid="admin-setrole-form">
      <form onSubmit={submit}>
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          <Field label="Nome utente (come appare nel registro)" id="setrole-login" value={login} onChange={setLogin} required placeholder="es. DennyWhiteWolf" />
          <div>
            <label className="hud-label block mb-2" htmlFor="setrole-role">Nuova fazione</label>
            <select id="setrole-role" value={role} onChange={(e) => setRole(e.target.value)} data-testid="setrole-role-input" className="w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300">
              <option value="lonestar">Lonestar</option>
              <option value="valkyra">Valkyra</option>
              <option value="manticore">Manticore</option>
            </select>
          </div>
        </div>
        <SaveButton label="Cambia fazione" busy={busy} testid="admin-setrole-button" />
      </form>
    </Box>
    <Box title="Segna come donatore — per le donazioni esterne (colletta PayPal, bonifico, contanti): badge dorato sul sito + ruolo Donatori su Discord se ha collegato l'account" testid="admin-setdonor-form">
      <form onSubmit={submitDonor}>
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          <Field label="Nome utente (come appare nel registro)" id="setdonor-login" value={donorLogin} onChange={setDonorLogin} required placeholder="es. DennyWhiteWolf" />
        </div>
        <SaveButton label="Segna come donatore" busy={donorBusy} testid="admin-setdonor-button" />
      </form>
    </Box>
    </>
  );
}

const ACTIVITY_STYLE = {
  registrazione: { label: "Registrazione", color: "#34C759" },
  accesso: { label: "Accesso", color: "#8E949B" },
  fazione: { label: "Fazione", color: "#FF5C00" },
  collegamento_social: { label: "Social collegato", color: "#00A3FF" },
  collegamento_discord: { label: "Discord collegato", color: "#5865F2" },
  collegamento_steam: { label: "Steam collegato", color: "#66C0F4" },
  scollegamento_discord: { label: "Discord scollegato", color: "#E8B23A" },
  scollegamento_steam: { label: "Steam scollegato", color: "#E8B23A" },
  aggiornamento_social: { label: "Social aggiornati", color: "#8E949B" },
  eliminazione_account: { label: "Account eliminato", color: "#FF3B30" },
  ban_utente: { label: "Utente bannato", color: "#FF3B30" },
  unban_utente: { label: "Ban rimosso", color: "#34C759" },
  allarme_multiaccount: { label: "ALLARME multi-account", color: "#FF3B30" },
  allarme_rafica: { label: "ALLARME raffica", color: "#FF3B30" },
};

function ActivityFeed({ onSelectUser }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/activity", { headers: headers() })
      .then(({ data }) => { if (alive) setItems(data.activity); })
      .catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
  return (
    <Box title="Attività utenti in tempo reale — ogni azione appare qui entro 5 secondi (solo staff)" testid="admin-activity-feed">
      {!items ? (
        <p className="font-mono text-xs text-steel">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="font-mono text-xs text-steel" data-testid="activity-empty">Nessuna attività registrata finora.</p>
      ) : (
        <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto pr-1" data-lenis-prevent data-testid="activity-list">
          {items.map((a, i) => {
            const s = ACTIVITY_STYLE[a.action] || { label: a.action, color: "#8E949B" };
            return (
              <div key={`${a.at}-${i}`} className="chamfer-sm border border-[#1A1A1A] bg-[#050505] px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`activity-item-${i}`}>
                <span className="chamfer-sm px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] font-bold" style={{ color: s.color, border: `1px solid ${s.color}55`, background: `${s.color}14` }}>
                  {s.label}
                </span>
                <button
                  onClick={() => onSelectUser && onSelectUser(a.login)}
                  className="font-mono text-[11px] text-white font-bold hover:text-wardogs transition-colors duration-300 underline decoration-dotted underline-offset-4"
                  title="Vedi questo utente nel registro accessi"
                  data-testid={`activity-user-${a.login}`}
                >
                  {a.display_name}
                </button>
                <span className="font-mono text-[10px] text-steel">({a.login})</span>
                {a.detail && <span className="font-mono text-[10px] text-steel break-all">{a.detail}</span>}
                <span className="font-mono text-[9px] text-[#4A4D51] ml-auto shrink-0">{fmtTime(a.at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Box>
  );
}

// Diagnostica collegamenti social (TikTok & co.): ultimi tentativi con esito.
// outcome "started" = il provider non ha MAI richiamato il nostro callback.
function SocialAttemptsBox() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/social-attempts", { headers: headers() }).then(({ data }) => setRows(data.attempts || [])).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  if (!rows || !rows.length) return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
  return (
    <Box title="Diagnostica collegamenti social — ultimi tentativi (solo staff)" testid="admin-social-attempts">
      <div className="space-y-1.5" data-testid="social-attempts-list">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-x-3 font-mono text-[11px] border-b border-[#1A1A1A] pb-1.5" data-testid={`social-attempt-${i}`}>
            <span className="text-steel">{fmt(r.started_at)}</span>
            <span className="text-white uppercase font-bold">{r.provider}</span>
            <span className="text-steel">{r.host}</span>
            <span className={`font-bold ${r.outcome === "started" ? "text-yellow-500" : r.outcome?.startsWith("linked") ? "text-green-500" : "text-red-500"}`}>
              {r.outcome === "started" ? "IN CORSO / MAI TORNATO" : r.outcome}
            </span>
            {r.finished_at && <span className="text-steel">→ {fmt(r.finished_at)}</span>}
          </div>
        ))}
      </div>
    </Box>
  );
}

// Verifica abbonamento di un utente Twitch (lato canale, serve il re-login del proprietario con ?broadcaster=1)
function SubCheckBox() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/twitch-sub-check?login=${encodeURIComponent(q.trim())}`, { headers: headers() });
      setRes(data);
    } catch {
      toast.error("Verifica non riuscita.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box title="Verifica abbonato — un utente risulta sub al canale? (solo staff)" testid="admin-sub-check">
      <form onSubmit={check} className="flex gap-2 mb-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="nome utente Twitch (es. fratrehh)"
          data-testid="sub-check-input"
          className="flex-1 bg-[#050505] border border-[#1A1A1A] px-3 py-2 font-mono text-xs text-white focus:border-wardogs outline-none"
        />
        <button type="submit" disabled={busy} data-testid="sub-check-button"
          className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 transition-colors duration-300">
          {busy ? "..." : "Verifica"}
        </button>
      </form>
      {res && (
        <p className="font-mono text-[11px]" data-testid="sub-check-result">
          {!res.found
            ? <span className="text-red-500">{res.login}: utente inesistente su Twitch</span>
            : res.subscribed === null
              ? <span className="text-yellow-500">{res.login}: verifica non attiva — il proprietario deve rientrare una volta dal link broadcaster</span>
              : res.subscribed
                ? <span className="text-green-500">{res.login}: ABBONATO ✓ (tier {res.tier}{res.is_gift ? ", regalata" : ""}{res.tier === "1000" ? " — puo' essere anche Prime" : ""})</span>
                : <span className="text-red-500">{res.login}: NON abbonato</span>}
        </p>
      )}
    </Box>
  );
}

function AccessiTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [openLogin, setOpenLogin] = useState(null);
  const [geo, setGeo] = useState({});

  const load = () => {
    setError(false);
    api.get("/admin/audit", { headers: headers() })
      .then(({ data }) => setData(data))
      .catch(() => {
        setError(true);
        toast.error("Registro accessi non caricato: reinserisci la chiave admin qui sopra e riprova.");
      });
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const toggleDetail = async (login) => {
    if (openLogin === login) { setOpenLogin(null); return; }
    setOpenLogin(login);
    if (!geo[login]) {
      setGeo((g) => ({ ...g, [login]: { loading: true } }));
      try {
        const { data } = await api.get(`/admin/audit/${login}/geo`, { headers: headers() });
        setGeo((g) => ({ ...g, [login]: { loading: false, data } }));
      } catch {
        setGeo((g) => ({ ...g, [login]: { loading: false, error: true } }));
      }
    }
  };

  const users = data?.users || [];
  const toggleBan = async (u) => {
    const azione = u.banned ? "sbannare" : "bannare";
    if (!window.confirm(`Confermi di voler ${azione} ${u.display_name}?${u.banned ? "" : " Non potrà più accedere né compiere azioni, e sparirà da fazioni e classifica."}`)) return;
    try {
      await api.post(`/admin/users/${u.login}/ban`, { banned: !u.banned }, { headers: headers() });
      toast.success(u.banned ? "Ban rimosso: l'utente può di nuovo accedere." : "Utente bannato: accesso e azioni bloccati.");
      load();
    } catch {
      toast.error("Operazione non riuscita.");
    }
  };
  const counts = users.reduce((acc, u) => { acc[u.provider] = (acc[u.provider] || 0) + 1; return acc; }, {});
  const filtered = users.filter((u) => filter === "all" || u.provider === filter);
  const providerStyle = {
    twitch: { label: "Twitch", color: "#9146FF" },
    discord: { label: "Discord", color: "#5865F2" },
    google: { label: "Google", color: "#EA4335" },
  };
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  const focusUser = (login) => {
    setFilter("all");
    setOpenLogin((prev) => (prev === login ? prev : null));
    setTimeout(() => {
      toggleDetail(login);
      const el = document.querySelector(`[data-testid="accessi-user-${login}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  return (
    <>
      <ActivityFeed onSelectUser={focusUser} />
      <SubCheckBox />
      <SocialAttemptsBox />
      <Box title="Registro accessi — identità social di ogni utente (solo staff)" testid="admin-accessi">
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="accessi-filters">
        {[["all", `Tutti (${users.length})`], ["twitch", `Twitch (${counts.twitch || 0})`], ["discord", `Discord (${counts.discord || 0})`], ["google", `Google (${counts.google || 0})`]].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            data-testid={`accessi-filter-${id}`}
            className={`chamfer-sm font-mono text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2 transition-colors duration-300 ${filter === id ? "bg-wardogs text-[#050505]" : "border border-[#2A2A2A] text-steel hover:text-white"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {error ? (
        <div className="text-center py-8" data-testid="accessi-error">
          <p className="font-mono text-xs text-valkyra mb-4">Caricamento fallito — chiave admin mancante o errore di rete.</p>
          <button onClick={load} data-testid="accessi-retry" className="chamfer-sm border border-wardogs/50 text-wardogs font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-wardogs hover:text-[#050505] transition-colors duration-300">
            Riprova
          </button>
        </div>
      ) : !data ? (
        <p className="font-mono text-xs text-steel">Caricamento…</p>
      ) : filtered.length === 0 ? (
        <p className="font-mono text-xs text-steel">Nessun accesso registrato.</p>
      ) : (
        <div className="flex flex-col gap-3" data-testid="accessi-list">
          {filtered.map((u) => {
            const p = providerStyle[u.provider] || providerStyle.twitch;
            const g = geo[u.login];
            const expanded = openLogin === u.login;
            return (
              <div
                key={u.account_id}
                className={`chamfer-sm border bg-[#050505] p-4 cursor-pointer transition-colors duration-300 ${expanded ? "border-wardogs/50" : "border-[#1A1A1A] hover:border-[#2A2A2A]"}`}
                data-testid={`accessi-user-${u.login}`}
                onClick={() => toggleDetail(u.login)}
              >
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {u.avatar ? <img src={u.avatar} alt="" className="w-8 h-8 chamfer-sm" /> : <span className="w-8 h-8 chamfer-sm bg-[#1A1A1A]" />}
                  <span className="font-display text-lg uppercase">{u.display_name}</span>
                  <span className="chamfer-sm px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] font-bold" style={{ color: p.color, border: `1px solid ${p.color}55`, background: `${p.color}14` }} data-testid={`accessi-provider-${u.login}`}>
                    {p.label}
                  </span>
                  {u.is_sub && <span className="font-mono text-[9px] text-wardogs uppercase tracking-[0.15em]">SUB {u.sub_tier ? `T${String(u.sub_tier)[0]}` : ""}{u.sub_channel ? ` · ${u.sub_channel}` : ""}</span>}
                  {u.role && <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-steel">· {u.role}</span>}
                  {u.banned && <span className="chamfer-sm px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] font-bold text-[#FF3B30] border border-[#FF3B30]/50 bg-[#FF3B30]/10" data-testid={`accessi-banned-${u.login}`}>Bannato</span>}
                  <span className="ml-auto flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleBan(u); }}
                      className={`chamfer-sm px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] font-bold transition-colors duration-300 ${u.banned ? "border border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759] hover:text-[#050505]" : "border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"}`}
                      data-testid={`accessi-ban-${u.login}`}
                    >
                      {u.banned ? "Sblocca" : "Banna"}
                    </button>
                    <span className="font-mono text-[9px] text-steel">{expanded ? "▲ chiudi" : "▼ dettagli"}</span>
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-1.5 font-mono text-[11px] text-steel">
                  <span>Login: <span className="text-white">{u.login}</span></span>
                  <span>Email: <span className="text-white">{u.email || "—"}</span></span>
                  <span>ID account: <span className="text-white break-all">{u.account_id}</span></span>
                  <span>Discord: <span className="text-white">{u.discord_username ? `${u.discord_username} (${u.discord_id})` : u.provider === "discord" ? u.account_id.replace("discord_", "") : "non collegato"}</span></span>
                  <span>Steam: <span className="text-white">{u.steam_persona ? `${u.steam_persona} (${u.steam64})` : "non collegato"}</span></span>
                  <span>Presenze: <span className="text-white">{u.visits} giorni</span> · Serie: <span className="text-white">{u.streak}</span></span>
                  <span>Primo accesso: <span className="text-white">{fmt(u.created_at)}</span></span>
                  <span>Ultimo giorno attivo: <span className="text-white">{u.last_seen_date || "—"}</span></span>
                </div>
                {u.socials && Object.values(u.socials).some(Boolean) && (
                  <div className="mt-3 pt-3 border-t border-[#1A1A1A] flex flex-wrap gap-3 font-mono text-[10px] text-steel">
                    {Object.entries(u.socials).filter(([, v]) => v).map(([k, v]) => (
                      <span key={k} className="uppercase">{k}: <span className="text-white">{v}</span></span>
                    ))}
                  </div>
                )}
                {expanded && (
                  <div className="mt-4 pt-4 border-t border-[#1A1A1A]" data-testid={`accessi-geo-${u.login}`}>
                    <p className="hud-label mb-3">Connessione (visibile solo allo staff)</p>
                    {g?.loading && <p className="font-mono text-[11px] text-steel">Localizzazione in corso…</p>}
                    {g?.error && <p className="font-mono text-[11px] text-valkyra">Localizzazione non riuscita.</p>}
                    {g?.data && (
                      <div className="font-mono text-[11px] text-steel flex flex-col gap-1.5">
                        {g.data.geo_precise && (
                          <span>Posizione precisa (condivisa dall'utente): <span className="text-[#34C759]">{[g.data.geo_precise.city, g.data.geo_precise.region, g.data.geo_precise.country].filter(Boolean).join(", ") || "coordinate salvate"}</span>{g.data.geo_precise.at ? ` — ${fmt(g.data.geo_precise.at)}` : ""}</span>
                        )}
                        <span>Ultimo IP: <span className="text-white">{g.data.ip || "non ancora registrato"}</span></span>
                        {g.data.geo ? (
                          <>
                            <span>Zona approssimativa: <span className="text-wardogs">{g.data.geo.city}, {g.data.geo.regionName}, {g.data.geo.country}</span></span>
                            <span>Provider: <span className="text-white">{g.data.geo.isp}</span></span>
                          </>
                        ) : g.data.ip ? (
                          <span>Posizione non determinabile per questo IP.</span>
                        ) : (
                          <span>Nessuna connessione registrata finora: la posizione apparirà al prossimo accesso dell'utente.</span>
                        )}
                        <span className="text-[9px] text-[#4A4D51]">Zona da IP: approssimativa (sede del provider), mai salvata. Posizione precisa: condivisa volontariamente dall'utente via browser, visibile solo a te.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </Box>
    </>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [key, setKey] = useState(localStorage.getItem("admin_key") || "");
  const [saved, setSaved] = useState(!!localStorage.getItem("admin_key"));
  const [tab, setTab] = useState("partner");

  // Con la chiave admin si vede tutto. Un membro staff SENZA chiave vede solo le tab assegnate.
  const restricted = !saved && user?.is_staff && Array.isArray(user.staff_tabs);
  const visibleGroups = restricted
    ? TAB_GROUPS.map((g) => ({ ...g, items: g.items.filter((tb) => user.staff_tabs.includes(tb.id)) })).filter((g) => g.items.length)
    : TAB_GROUPS;

  useEffect(() => {
    if (restricted && !user.staff_tabs.includes(tab)) {
      const first = visibleGroups[0]?.items[0]?.id;
      if (first) setTab(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restricted, user?.staff_tabs]);

  if (loading) {
    return <main className="pt-32 pb-28 min-h-screen" data-testid="admin-loading" />;
  }
  if (!user || !user.is_staff) {
    return (
      <main className="pt-32 pb-28">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <p className="hud-label mb-3 text-valkyra">// ACCESSO NEGATO</p>
          <h1 className="font-display text-6xl uppercase leading-[0.9] mb-4">Zona <span className="text-valkyra">riservata</span></h1>
          <p className="text-steel max-w-lg" data-testid="admin-denied">
            Quest'area è accessibile solo allo staff del quartier generale. Se fai parte dello staff, accedi prima con il tuo account dal pulsante in alto.
          </p>
        </div>
      </main>
    );
  }

  const saveKey = (e) => {
    e.preventDefault();
    localStorage.setItem("admin_key", key.trim());
    setSaved(true);
    toast.success("Chiave salvata in questo browser.");
  };

  return (
    <main className="pt-32 pb-28">
      <div className="max-w-[1440px] mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">// AREA RISERVATA</p>
          <h1 className="font-display text-6xl uppercase leading-[0.9] mb-4">Pannello <span className="text-wardogs">comando</span></h1>
          <p className="text-steel max-w-lg mb-10">
            Da qui gestisci tutto il sito in totale autonomia, organizzato per aree: contenuti, community, sicurezza e sistema. La chiave admin resta salvata solo in questo browser.
          </p>
        </Reveal>

        {restricted ? (
          <Reveal>
            <div className="chamfer border border-wardogs/30 bg-wardogs/5 p-6 mb-10" data-testid="admin-restricted-note">
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-wardogs mb-1">Accesso staff con permessi assegnati</p>
              <p className="text-sm text-steel leading-relaxed">Vedi solo le sezioni che il Quartiermastro ti ha abilitato. Le modifiche che fai qui sono subito attive sul sito.</p>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={saveKey} className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-6 mb-10 flex flex-col md:flex-row gap-4 md:items-end" data-testid="admin-key-form">
              <div className="flex-1">
                <label className="hud-label block mb-2" htmlFor="admin-key">Chiave admin</label>
                <input
                  id="admin-key"
                  type="password"
                  value={key}
                  onChange={(e) => setKey(e.target.value)}
                  data-testid="admin-key-input"
                  placeholder="Incolla la chiave admin"
                  className="w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300"
                />
              </div>
              <button
                type="submit"
                data-testid="admin-key-save-button"
                className="chamfer-sm bg-wardogs hover:bg-wardogs-hover text-[#050505] font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300 flex items-center gap-2"
              >
                <KeyRound className="w-4 h-4" /> {saved ? "Aggiorna chiave" : "Salva chiave"}
              </button>
            </form>
          </Reveal>
        )}

        <div className="grid md:grid-cols-[230px_1fr] gap-8 items-start">
          <Reveal delay={0.05}>
            <nav className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] p-3 md:sticky md:top-28 flex md:flex-col gap-1 overflow-x-auto wheel-x" data-lenis-prevent data-testid="admin-tabs">
              {visibleGroups.map((g) => (
                <div key={g.group} className="flex md:flex-col gap-1">
                  <p className="hud-label text-[#4A4D51] px-3 pt-4 pb-2 hidden md:block" data-testid={`admin-group-${g.testid}`}>{g.group}</p>
                  {g.items.map((tb) => (
                    <button
                      key={tb.id}
                      onClick={() => setTab(tb.id)}
                      data-testid={`admin-tab-${tb.id}`}
                      className={`flex items-center gap-3 px-3 py-2.5 chamfer-sm font-mono text-[11px] uppercase tracking-[0.12em] whitespace-nowrap transition-colors duration-300 text-left ${
                        tab === tb.id ? "bg-wardogs text-[#050505] font-bold" : "text-steel hover:text-white hover:bg-[#1A1A1A]"
                      }`}
                    >
                      <tb.icon className="w-4 h-4 shrink-0" /> {tb.label}
                    </button>
                  ))}
                </div>
              ))}
            </nav>
          </Reveal>

          <div className="min-w-0">
            {tab === "partner" && <PartnersTab />}
            {tab === "giveaway" && <GiveawayTab />}
            {tab === "eventi" && <EventiTab />}
            {tab === "eventi-gaming" && <GameEventsTab />}
            {tab === "bot-annunci" && <BotTab />}
            {tab === "live-notify" && <LiveNotifyTab />}
            {tab === "link-discord" && <LinkDiscordTab />}
            {tab === "programmazione" && <ProgrammazioneTab />}
            {tab === "media" && <MediaTab />}
            {tab === "testi" && <TestiTab />}
            {tab === "paypal" && <PayPalTab />}
            {tab === "webhook" && <WebhookTab />}
            {tab === "countdown" && <CountdownTab />}
            {tab === "youtube" && <YouTubeTab />}
            {tab === "sito" && <SitoTab />}
            {tab === "staff" && <StaffTab />}
            {tab === "utenti" && <UtentiTab />}
            {tab === "accessi" && <AccessiTab />}
          </div>
        </div>
      </div>
    </main>
  );
}
